import { useState, useEffect, useMemo, useRef } from 'react';
import { X, Upload, Plus, Trash2, GripVertical, Euro, AlertCircle, Check, Search, Loader2 } from 'lucide-react';
import apiService from '../services/api';
import { ARCHETYPE_IDS } from '../config/produitArchetypes.js';
import { PRODUCT_SECTION_KEYS as SK } from '../config/produitSectionKeys.js';
import { useProduitArchetypeUi } from '../hooks/useProduitArchetypeUi.js';
import ProduitArchetypeBanner from './produit/ProduitArchetypeBanner';
import CaracteristiquesChips from './produit/CaracteristiquesChips';
import ProduitCasParticuliers from './produit/ProduitCasParticuliers';
import ProduitSaveBlockers from './produit/ProduitSaveBlockers';
import ProduitSectionHeader from './produit/ProduitSectionHeader';
import {
  listUncoveredLegacyBools,
  omitLegacyBoolFields,
} from '../config/caracteristiqueLegacyBools.js';
import {
  PRODUIT_FORM_ISSUE_IDS,
  getProduitFormBlockingIssues,
  scrollToProduitSection,
} from '../utils/produitFormValidation.js';
import {
  applyVenteAuPoidsToggle,
  areClassicPriceInputsInactive,
  cataloguePrixSentOnSave,
  classicPriceInputClassName,
  isTvaEditableWithWeight,
  loadDecoupesDisponiblesFromProduit,
  shouldMountPrixTarificationBlock,
} from '../utils/venteAuPoidsFormUx.js';
import {
  buildServiceProduitCreatePayload,
  buildServiceProduitUpdatePayload,
  getProduitModalTitle,
  isServiceTypeProduit,
  shouldShowSectionForProduitType,
} from '../utils/serviceProduitPayload.js';
import { buildServiceToPrestationConversionPayload } from '../utils/prestationConversionPayload.js';
import { getOffreLockedTypeLabel } from '../utils/offreCreateRouting.js';
import ConfirmDialog from './ui/ConfirmDialog';
import { NUMBER_INPUT_PROPS } from '../utils/numberInputHandlers.js';
import { buildPrixProHtSaveField, isB2bV1CompatibleProductForm, previewProTtc } from '../utils/b2bProduitScope.js';
import {
  getDefaultTauxTVA,
  isRestaurationCategorie,
  normalizeTauxTVAInput,
  TVA_RATE_OPTIONS,
} from '../utils/tvaDefaults';
import { API_BASE_URL } from '../config/api.config';
import ProduitFieldsCentreEquestre from './ProduitFieldsCentreEquestre';
import ProduitFieldsRestaurant from './ProduitFieldsRestaurant';
import PrixCanalSection from './produit/PrixCanalSection';
import Modal from './Modal';
import { uploadMultipleImagesToCloudinary } from '../utils/uploadCloudinary';
import {
  fetchAllCommercantProduitsForPicker,
  buildLinkedProduitPickerOptions,
} from '../utils/linkedProduitPickerCatalog.js';
import { serializeProduitOptionsForSave } from '../utils/produitOptionsSavePayload.js';
import {
  buildExpeditionProfileSelectOptions,
  parseExpeditionProfilesResponse,
} from '../utils/expeditionProductProfiles.js';
import ProduitIdentificationBarcodes from './produit/ProduitIdentificationBarcodes';

const ProduitModal = ({
  isOpen,
  onClose,
  produit,
  commercantId,
  /** Création : 'PHYSIQUE' | 'SERVICE' (catalogue vitrine, ≠ Prestation RDV) */
  initialTypeProduit = 'PHYSIQUE',
  onSave,
  initialCategories = [],
  archetypeId = null,
  archetypeAnswers = null,
  onRequestChangeArchetype,
  creationOptimized = false,
}) => {
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    codeBarres: '',
    referenceInterne: '',
    prix: '',
    prixPromo: '',
    prixProHt: '',
    categorie: '',
    sousCategorie: '',
    images: [],
    stock: -1,
    stockLimite: false,
    actif: true,
    indisponible: false,
    tempsPreparationPersonnalise: false,
    tempsPreparation: 15,
    vegetarien: false,
    vegan: false,
    sansGluten: false,
    // Nouveaux badges
    bio: false,
    local: false,
    directProducteur: false,
    epice: false,
    sansLactose: false,
    sansArachides: false,
    sansPoisson: false,
    ingredients: [],
    allergenes: [],
    // Type de produit (SERVICE = catalogue vitrine ; ≠ Prestation RDV)
    typeProduit: 'PHYSIQUE',
    // Type détaillé du produit (optionnel, pour rétrocompatibilité)
    productType: null, // SIMPLE, PLAT_CLASSIQUE, MENU_COMBO, PLATEAU_COMPOSE, PAR_PERSONNE, PAR_POIDS, PAR_UNITE, PRODUCTEUR_LOCAL, COMMERCE_NON_FOOD
    // Toggle simplifié pour les plateaux par personne
    isPlateauParPersonne: false,
    minPersons: 2,
    maxPersons: 20,
    prixParPersonne: null,
    // Mode de composition pour PAR_PERSONNE (FIXED = seulement variée, COMPOSABLE = variée + personnalisée)
    compositionMode: null, // null, 'FIXED', ou 'COMPOSABLE'
    // Ingrédients disponibles pour la planche (IDs stockés en JSON dans preparationRequis)
    ingredientIdsPlanche: [], // IDs des ingrédients que le client peut choisir pour la planche personnalisée
    // Champs spécifiques aux services
    dureeMinutes: null,
    minParticipants: 1,
    maxParticipants: 1,
    equipementFourni: false,
    preparationRequis: '',
    // Mode composition
    modeComposition: false,
    incrementPieces: 1,
    limiteTotal: null,
    validationMode: 'FLEXIBLE',
    // TVA
    tauxTVA: 20,
    // Vente au poids
    venteAuPoids: false,
    prixAuPoids: '',
    unitePoids: 'kg',
    poidsPortionStandard: '',
    nombreTranchesParPortion: '',
    decoupesDisponibles: [],
    // A2.0 Expédition
    shippingProfileId: null,
    shippingWeightGrams: '',
  });

  const [options, setOptions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [convertingToReservable, setConvertingToReservable] = useState(false);
  /** Verrou synchrone : React state `saving` ne protège pas contre un double-clic avant re-render */
  const savingLockRef = useRef(false);
  const createIdempotencyKeyRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Nouvelles fonctionnalités avancées
  const [templates, setTemplates] = useState([]);
  const [contraintes, setContraintes] = useState([]);
  const [elementsComposables, setElementsComposables] = useState([]);
  const [taillesPlateaux, setTaillesPlateaux] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingElements, setLoadingElements] = useState(false);
  const [loadingTailles, setLoadingTailles] = useState(false);
  const [ingredientsCatalog, setIngredientsCatalog] = useState([]);
  const [ingredientsLoading, setIngredientsLoading] = useState(false);
  const [ingredientsError, setIngredientsError] = useState('');
  const [ingredientsSearch, setIngredientsSearch] = useState('');
  const [selectedIngredientIds, setSelectedIngredientIds] = useState([]);
  const [showCreateIngredientModal, setShowCreateIngredientModal] = useState(false);
  const [newIngredientData, setNewIngredientData] = useState({
    nom: '',
    description: '',
    allergenes: []
  });
  const [newIngredientAllergenes, setNewIngredientAllergenes] = useState('');
  const [creatingIngredient, setCreatingIngredient] = useState(false);
  const [ingredientNameError, setIngredientNameError] = useState('');
  // États pour la gestion des plateaux disponibles
  const [plateauxDisponibles, setPlateauxDisponibles] = useState([]); // Liste des produits PAR_PERSONNE du commerçant
  const [loadingPlateaux, setLoadingPlateaux] = useState(false);
  const [produitsCatalogueStock, setProduitsCatalogueStock] = useState([]);
  const [loadingProduitsCatalogueStock, setLoadingProduitsCatalogueStock] = useState(false);
  const [plateauxIdsDisponibles, setPlateauxIdsDisponibles] = useState([]); // IDs des plateaux où ce produit est disponible
  /** Booléens historiques actifs sans caractéristique — reprise manuelle (pas de backfill). */
  const [legacyOrphans, setLegacyOrphans] = useState([]);
  // A2.0 Expédition — profils colis (moduleExpedition)
  const [moduleExpeditionEnabled, setModuleExpeditionEnabled] = useState(false);
  const [shippingProfiles, setShippingProfiles] = useState([]);
  const [loadingShippingProfiles, setLoadingShippingProfiles] = useState(false);
  const [shippingProfilesLoadError, setShippingProfilesLoadError] = useState('');

  const {
    enrichExpanded,
    setEnrichExpanded,
    revealedSections,
    changeArchetypeConfirmOpen,
    setChangeArchetypeConfirmOpen,
    showSec,
    showStandaloneTva,
    guidedMode,
    lockCompositionToggle,
    lockPlancheToggle,
    lockVenteAuPoidsToggle,
    formCanSubmit,
    formBlockingIssues,
    handleRevealSection,
    handleChangeArchetypeRequest,
    confirmChangeArchetype,
    isCreationMode,
    showAllSections,
    handleCreationShortcutToggle,
    handleShowAllSections,
    handleShowSimplified,
  } = useProduitArchetypeUi({
    isOpen,
    produit,
    creationOptimized,
    archetypeId,
    archetypeAnswers,
    formData,
    taillesPlateaux,
    options,
    setFormData,
    onRequestChangeArchetype,
  });

  const isService = isServiceTypeProduit(formData.typeProduit);
  const showSecForType = (sectionKey) =>
    shouldShowSectionForProduitType(formData.typeProduit, sectionKey, showSec);
  const [serviceAdvancedOpen, setServiceAdvancedOpen] = useState(false);

  // Empêcher le scroll de modifier les inputs numériques sensibles (prix, poids, etc.)
  const preventNumberScroll = (event) => {
    if (!event) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget?.blur();
  };

  const hasFormBlocker = (issueId) =>
    !formCanSubmit && formBlockingIssues.some((issue) => issue.id === issueId);

  const formFieldErrorClass = (...issueIds) =>
    issueIds.some((id) => hasFormBlocker(id))
      ? 'border-red-400 focus:ring-red-200'
      : 'border-gray-300';

  // Dialog d'erreur détaillée (pour pouvoir copier facilement le message complet)
  const [codeBarresError, setCodeBarresError] = useState('');
  const [internalBarcode, setInternalBarcode] = useState(null);
  const [choixInternalById, setChoixInternalById] = useState({});

  const [errorDialog, setErrorDialog] = useState({
    open: false,
    title: '',
    message: '',
    raw: null
  });
  
  // Type de commerce pour adapter l'interface
  const [commercantType, setCommercantType] = useState(null);
  const [moduleB2bEnabled, setModuleB2bEnabled] = useState(false);
  
  const [commercantDefaultPrep, setCommercantDefaultPrep] = useState(15);

  useEffect(() => {
    if (!isOpen || !commercantId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getCommercant(commercantId);
        const c = data?.commercant || data;
        if (!cancelled) {
          setModuleB2bEnabled(Boolean(c?.moduleB2b));
          setModuleExpeditionEnabled(Boolean(c?.moduleExpedition));
        }
        const prep = Number(c?.tempsPreparation);
        if (!cancelled && Number.isFinite(prep) && prep >= 0) {
          setCommercantDefaultPrep(prep);
        }
      } catch {
        /* défaut 15 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, commercantId]);

  // A2.0 — charger profils colis uniquement si module Expédition actif
  useEffect(() => {
    if (!isOpen || !commercantId || !moduleExpeditionEnabled) {
      setShippingProfiles([]);
      setShippingProfilesLoadError('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingShippingProfiles(true);
      setShippingProfilesLoadError('');
      try {
        const res = await apiService.listExpeditionProfiles(commercantId, {
          includeInactive: true,
        });
        // Contrat API : { items: Profile[] } — pas { profiles }
        const list = parseExpeditionProfilesResponse(res);
        if (!cancelled) setShippingProfiles(list);
      } catch (err) {
        console.error('❌ [ProduitModal] Erreur chargement profils colis:', err);
        if (!cancelled) {
          setShippingProfiles([]);
          setShippingProfilesLoadError(
            err?.message || 'Impossible de charger les profils colis.',
          );
        }
      } finally {
        if (!cancelled) setLoadingShippingProfiles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, commercantId, moduleExpeditionEnabled]);

  // Catégories existantes du commerçant
  const [existingCategories, setExistingCategories] = useState(initialCategories || []);
  const [catalogueTree, setCatalogueTree] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showNewSubCategoryInput, setShowNewSubCategoryInput] = useState(false);

  const selectedCategoryNode = catalogueTree.find((c) => c.nom === formData.categorie);
  const existingSubCategories = (selectedCategoryNode?.sousCategories || [])
    .map((s) => s.nom)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));

  useEffect(() => {
    if (produit) {
      setFormData({
        nom: produit.nom || '',
        description: produit.description || '',
        codeBarres: produit.codeBarres || '',
        referenceInterne: produit.referenceInterne || '',
        prix: produit.prix !== undefined && produit.prix !== null ? String(produit.prix) : '',
        prixPromo: produit.prixPromo !== undefined && produit.prixPromo !== null ? String(produit.prixPromo) : '',
        prixProHt: produit.prixProHt !== undefined && produit.prixProHt !== null ? String(produit.prixProHt) : '',
        categorie: produit.categorie || '',
        sousCategorie: produit.sousCategorie || '',
        images: produit.images || [],
        stock: produit.stock !== undefined && produit.stock !== null ? produit.stock : -1,
        stockLimite: produit.stock !== undefined && produit.stock !== null && produit.stock !== -1,
        actif: produit.actif ?? true,
        indisponible: produit.indisponible ?? false,
        tempsPreparationPersonnalise: Boolean(produit.tempsPreparationPersonnalise),
        tempsPreparation: produit.tempsPreparation ?? 15,
        vegetarien: produit.vegetarien || false,
        vegan: produit.vegan || false,
        sansGluten: produit.sansGluten || false,
        // Nouveaux badges
        bio: produit.bio || false,
        local: produit.local || false,
        directProducteur: produit.directProducteur || false,
        epice: produit.epice || false,
        sansLactose: produit.sansLactose || false,
        sansArachides: produit.sansArachides || false,
        sansPoisson: produit.sansPoisson || false,
        allergenes: produit.allergenes || [],
        // Type de produit
        typeProduit: produit.typeProduit || 'PHYSIQUE',
        // Type détaillé du produit (optionnel, NULL pour les anciens produits)
        productType: produit.productType || null,
        // Toggle simplifié pour les plateaux par personne
        isPlateauParPersonne: produit.productType === 'PAR_PERSONNE',
        minPersons: produit.minPersons || 2,
        maxPersons: produit.maxPersons || 20,
        prixParPersonne: produit.prixParPersonne || null,
        // Mode de composition pour PAR_PERSONNE
        compositionMode: produit.compositionMode || null, // null, 'FIXED', ou 'COMPOSABLE'
        // Ingrédients disponibles pour la planche (nouvelle relation ProduitIngredientPlanche)
        ingredientIdsPlanche: (() => {
          const raw = produit?.ingredientsPlanche || [];
          const extractedIds = raw
            .map((ing) => {
              // ing est un ProduitIngredientPlanche
              const id =
                ing?.ingredientProduit?.id || // cas normal avec include { ingredientProduit }
                ing?.ingredientId ||          // fallback si pas d'include
                (typeof ing === 'string' ? ing : ing?.id);
              return id || null;
            })
            .filter(Boolean);
          return extractedIds;
        })(),
        // Champs spécifiques aux services
        dureeMinutes: produit.dureeMinutes || null,
        minParticipants: produit.minParticipants || 1,
        maxParticipants: produit.maxParticipants || 1,
        equipementFourni: produit.equipementFourni || false,
        preparationRequis: produit.preparationRequis || '',
        modeComposition: produit.modeComposition || false,
        incrementPieces: produit.incrementPieces || 1,
        limiteTotal: produit.limiteTotal || null,
        validationMode: produit.validationMode || 'FLEXIBLE',
        tauxTVA: produit.tauxTVA ?? getDefaultTauxTVA(commercantType),
        // Vente au poids
        venteAuPoids: produit.venteAuPoids || false,
        prixAuPoids: produit.prixAuPoids !== undefined && produit.prixAuPoids !== null ? String(produit.prixAuPoids) : '',
        unitePoids: produit.unitePoids || 'kg',
        poidsPortionStandard: produit.poidsPortionStandard !== undefined && produit.poidsPortionStandard !== null ? String(produit.poidsPortionStandard) : '',
        nombreTranchesParPortion: produit.nombreTranchesParPortion !== undefined && produit.nombreTranchesParPortion !== null ? String(produit.nombreTranchesParPortion) : '',
        decoupesDisponibles: loadDecoupesDisponiblesFromProduit(produit),
        shippingProfileId: produit.shippingProfileId || null,
        shippingWeightGrams:
          produit.shippingWeightGrams != null ? String(produit.shippingWeightGrams) : '',
      });
      setOptions((produit.options || []).map(opt => ({
        ...opt,
        type: opt.type || (opt.choixMultiple ? 'CHOIX_MULTIPLE' : 'CHOIX_UNIQUE'),
        choixMultiple: opt.type ? opt.type === 'CHOIX_MULTIPLE' : Boolean(opt.choixMultiple),
        nbInclus: opt.nbInclus ?? 0,
        maxIllimite: opt.type === 'CHOIX_MULTIPLE' || opt.choixMultiple
          ? opt.choixMaximum == null
          : false,
      })));
      setInternalBarcode(produit.internalBarcode || null);
      const choixMap = {};
      for (const opt of produit.options || []) {
        for (const choix of opt.choix || []) {
          if (choix?.id && choix.internalBarcode) {
            choixMap[choix.id] = choix.internalBarcode;
          }
        }
      }
      setChoixInternalById(choixMap);
      setContraintes(produit.contraintes || []);
      const relationIds = new Set();
      const ingredientsFromProduit = [];
      
      if (Array.isArray(produit.ingredients)) {
        produit.ingredients.forEach((item) => {
          let ingredientId = null;
          let ingredientData = null;
          
          if (item?.ingredientId) {
            ingredientId = item.ingredientId;
            ingredientData = item?.ingredient || null;
          } else if (item?.ingredient?.id) {
            ingredientId = item.ingredient.id;
            ingredientData = item.ingredient;
          } else if (item?.id) {
            ingredientId = item.id;
            ingredientData = item;
          }
          
          if (ingredientId) {
            relationIds.add(ingredientId);
            // ⭐ Enrichir le catalogue avec les ingrédients du produit
            if (ingredientData) {
              ingredientsFromProduit.push({
                id: ingredientId,
                nom: ingredientData.nom || '',
                description: ingredientData.description || null,
                allergenes: ingredientData.allergenes || [],
                actif: ingredientData.actif !== false,
                categorie: ingredientData.categorie || null,
                commercantId: ingredientData.commercantId || null
              });
            }
          }
        });
      }
      
      setSelectedIngredientIds(Array.from(relationIds));
      setLegacyOrphans(listUncoveredLegacyBools(produit));
      
      // ⭐ Stocker les ingrédients du produit pour les ajouter au catalogue une fois qu'il est chargé
      // (on ne peut pas les ajouter maintenant car le catalogue n'est pas encore chargé)
      
      // Les ingrédients sont maintenant gérés via selectedIngredientIds
      // Plus besoin de manualIngredients
    } else {
      // Reset pour nouveau produit
      setFormData({
        nom: '',
        description: '',
        prix: '',
        prixPromo: '',
        prixProHt: '',
        categorie: '',
        sousCategorie: '',
        images: [],
        stock: -1,
        stockLimite: false,
        actif: true,
        indisponible: false,
        vegetarien: false,
        vegan: false,
        sansGluten: false,
        // Nouveaux badges
        bio: false,
        local: false,
        directProducteur: false,
        epice: false,
        sansLactose: false,
        sansArachides: false,
        sansPoisson: false,
        allergenes: [],
        // Type de produit (création via initialTypeProduit)
        typeProduit: isServiceTypeProduit(initialTypeProduit)
          ? 'SERVICE'
          : 'PHYSIQUE',
        // Type détaillé du produit (optionnel, NULL par défaut)
        productType: null,
        // Toggle simplifié pour les plateaux par personne
        isPlateauParPersonne: false,
        minPersons: 2,
        maxPersons: 20,
        prixParPersonne: null,
        // Ingrédients disponibles pour la planche
        ingredientIdsPlanche: [],
        // Champs spécifiques aux services
        dureeMinutes: null,
        minParticipants: 1,
        maxParticipants: 1,
        equipementFourni: false,
        preparationRequis: '',
        modeComposition: false,
        incrementPieces: 1,
        limiteTotal: null,
        validationMode: 'FLEXIBLE',
        // Vente au poids
        venteAuPoids: false,
        prixAuPoids: '',
        unitePoids: 'kg',
        poidsPortionStandard: '',
        nombreTranchesParPortion: '',
        decoupesDisponibles: [],
        shippingProfileId: null,
        shippingWeightGrams: '',
      });
      setOptions([]);
      setContraintes([]);
      setSelectedIngredientIds([]);
      setLegacyOrphans([]);
      // Plus besoin de réinitialiser manualIngredients
    }
  }, [produit, isOpen]);

  // Charger les templates et éléments composables au montage du modal
  useEffect(() => {
    if (isOpen) {
      // Réinitialiser les catégories connues à partir du parent à chaque ouverture
      setExistingCategories(initialCategories || []);
      loadCommercantType();
      loadTemplates();
      if (produit?.id) {
        loadElementsComposables();
        loadTaillesPlateaux();
      }
      // Pour les planches PAR_PERSONNE, charger les produits comme ingrédients
      // Sinon, charger les ingrédients classiques
      if (produit?.productType === 'PAR_PERSONNE') {
        loadProduitsIngredientsPlanche();
      } else {
        loadIngredientsCatalog();
      }
      loadCategories();
      loadPlateauxDisponibles(); // Charger les plateaux pour la section "Disponible sur plateaux"
      loadProduitsCatalogueStock();
    }
  }, [isOpen, produit?.id, commercantId, produit?.productType, formData.isPlateauParPersonne, initialTypeProduit]);
  
  // Recharger le catalogue quand on bascule vers/depuis le mode planche
  useEffect(() => {
    if (!isOpen) return;
    if (formData.isPlateauParPersonne) {
      loadProduitsIngredientsPlanche();
    } else {
      loadIngredientsCatalog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isPlateauParPersonne, commercantId, produit?.id]);

  // ⭐ Enrichir le catalogue avec les ingrédients du produit une fois que le catalogue est chargé
  useEffect(() => {
    if (!produit) {
      console.log('🔍 [ProduitModal] Pas de produit');
      return;
    }
    if (!produit.ingredients) {
      console.log('🔍 [ProduitModal] Produit sans ingrédients:', produit.id);
      return;
    }
    if (ingredientsLoading) {
      console.log('🔍 [ProduitModal] Catalogue en cours de chargement...');
      return;
    }
    if (ingredientsCatalog.length === 0) {
      console.log('🔍 [ProduitModal] Catalogue vide, attendre le chargement');
      return;
    }
    
    console.log('🔍 [ProduitModal] Enrichissement du catalogue:', {
      produitId: produit.id,
      ingredientsCount: produit.ingredients?.length || 0,
      catalogueCount: ingredientsCatalog.length,
      ingredients: produit.ingredients
    });
    
    const ingredientsFromProduit = [];
    if (Array.isArray(produit.ingredients)) {
      produit.ingredients.forEach((item) => {
        let ingredientId = null;
        let ingredientData = null;
        
        if (item?.ingredientId) {
          ingredientId = item.ingredientId;
          ingredientData = item?.ingredient || null;
        } else if (item?.ingredient?.id) {
          ingredientId = item.ingredient.id;
          ingredientData = item.ingredient;
        } else if (item?.id) {
          ingredientId = item.id;
          ingredientData = item;
        }
        
        if (ingredientId && ingredientData) {
          ingredientsFromProduit.push({
            id: ingredientId,
            nom: ingredientData.nom || '',
            description: ingredientData.description || null,
            allergenes: ingredientData.allergenes || [],
            actif: ingredientData.actif !== false,
            categorie: ingredientData.categorie || null,
            commercantId: ingredientData.commercantId || null
          });
        }
      });
    }
    
    console.log('🔍 [ProduitModal] Ingrédients extraits du produit:', ingredientsFromProduit);
    
    // Ajouter les ingrédients du produit au catalogue s'ils n'y sont pas déjà
    if (ingredientsFromProduit.length > 0) {
      setIngredientsCatalog((prevCatalog) => {
        const existingIds = new Set(prevCatalog.map(ing => ing.id));
        const newIngredients = ingredientsFromProduit.filter(ing => !existingIds.has(ing.id));
        if (newIngredients.length > 0) {
          console.log('✅ [ProduitModal] Ajout de', newIngredients.length, 'ingrédients du produit au catalogue');
          return [...prevCatalog, ...newIngredients];
        } else {
          console.log('ℹ️ [ProduitModal] Tous les ingrédients du produit sont déjà dans le catalogue');
        }
        return prevCatalog;
      });
    } else {
      console.log('⚠️ [ProduitModal] Aucun ingrédient extrait du produit');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produit?.ingredients, ingredientsCatalog.length, ingredientsLoading]);
  
  const loadCategories = async () => {
    if (!commercantId) return;
    try {
      setLoadingCategories(true);
      const data = await apiService.getCatalogueRubriques(commercantId);
      const categories = Array.isArray(data?.categories) ? data.categories : [];
      setCatalogueTree(categories);
      const names = categories.map((c) => c.nom).filter((n) => n && n.trim());
      const merged = Array.from(
        new Set(
          [...names, ...(initialCategories || [])].filter(
            (c) => typeof c === 'string' && c.trim().length > 0
          )
        )
      ).sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
      setExistingCategories(merged);
    } catch (error) {
      console.error('Erreur lors du chargement du catalogue rubriques:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const ensureCatalogueRubrique = async (niveau, nom, parentId = null) => {
    if (!commercantId || !nom?.trim()) return;
    try {
      await apiService.createCatalogueRubrique(commercantId, {
        niveau,
        nom: nom.trim(),
        ...(parentId ? { parentId } : {}),
      });
      await loadCategories();
    } catch (error) {
      console.error('Erreur création rubrique catalogue:', error);
    }
  };

  const loadCommercantType = async () => {
    try {
      const response = await apiService.getCommercant(commercantId);
      const cat = response.categorie;
      setCommercantType(cat);
      if (!produit?.id) {
        setFormData((prev) => ({
          ...prev,
          tauxTVA: getDefaultTauxTVA(cat),
        }));
      }
    } catch (error) {
      console.error('Erreur chargement type commerce:', error);
      setCommercantType(null);
    }
  };

  // Charger les produits disponibles comme ingrédients de planche (pour produits PAR_PERSONNE)
  const loadProduitsIngredientsPlanche = async () => {
    if (!commercantId) return;
    try {
      setIngredientsLoading(true);
      setIngredientsError('');
      
      // ⭐ Récupérer TOUTES les pages de produits (pagination complète)
      let allProduits = [];
      let page = 1;
      let hasMore = true;
      const pageSize = 50; // Maximum autorisé par le backend
      
      while (hasMore) {
        // ⭐ Utiliser includeInactifs=true pour récupérer TOUS les produits, même inactifs
        const response = await apiService.getProduitsByCommercant(commercantId, true, { page, pageSize });
        
        const produitsPage = Array.isArray(response?.produits) 
          ? response.produits 
          : Array.isArray(response) 
          ? response 
          : [];
        
        allProduits = [...allProduits, ...produitsPage];
        
        // Vérifier s'il y a encore des produits à récupérer
        const total = response?.total || response?.pagination?.total || 0;
        hasMore = allProduits.length < total && produitsPage.length === pageSize;
        page++;
      }
      
      // Filtrer uniquement pour exclure le produit actuel et les plateaux PAR_PERSONNE
      // ⭐ IMPORTANT : Ne PAS filtrer sur actif pour voir TOUS les produits du commerçant
      const produitsDisponibles = allProduits.filter((p) => {
        if (p.id === produit?.id) return false; // Ne pas s'auto-utiliser
        if (p.productType === 'PAR_PERSONNE') return false; // Exclure les plateaux
        return true; // ⭐ Inclure tous les autres produits, même inactifs
      });
      
      console.log(`✅ [ProduitModal] ${allProduits.length} produits chargés, ${produitsDisponibles.length} disponibles comme ingrédients`);
      setIngredientsCatalog(produitsDisponibles);
    } catch (e) {
      console.error('❌ Erreur loadProduitsIngredientsPlanche', e);
      setIngredientsError('Erreur lors du chargement des produits disponibles');
    } finally {
      setIngredientsLoading(false);
    }
  };

  const loadIngredientsCatalog = async () => {
    try {
      setIngredientsLoading(true);
      setIngredientsError('');
      
      console.log('🔍 [ProduitModal] Chargement du catalogue d\'ingrédients, commercantId:', commercantId);
      
      // ⭐ Utiliser all=true pour charger TOUS les ingrédients d'un coup (admin uniquement)
      const endpoint = commercantId 
        ? `/ingredients?commercantId=${commercantId}&all=true`
        : `/ingredients?all=true`;
      
      console.log('🔍 [ProduitModal] Appel API (tous les ingrédients):', endpoint);
      const response = await apiService.request(endpoint);
      
      // Structure de réponse : { data: [...], pagination: { total, ... } }
      const allIngredients = Array.isArray(response?.data) ? response.data : [];
      
      console.log('✅ [ProduitModal] Catalogue chargé:', allIngredients.length, 'ingrédients');
      setIngredientsCatalog(allIngredients);
    } catch (error) {
      console.error('❌ [ProduitModal] Erreur lors du chargement des ingrédients:', error);
      setIngredientsCatalog([]);
      setIngredientsError("Impossible de charger la liste des ingrédients. Vérifiez l'onglet Ingrédients.");
    } finally {
      setIngredientsLoading(false);
    }
  };

  const loadProduitsCatalogueStock = async () => {
    if (!commercantId) return;
    try {
      setLoadingProduitsCatalogueStock(true);
      const produitsList = await fetchAllCommercantProduitsForPicker(
        apiService.getProduitsByCommercant.bind(apiService),
        commercantId
      );
      const catalogue = buildLinkedProduitPickerOptions(produitsList, {
        excludeProduitId: produit?.id ?? null,
      });
      setProduitsCatalogueStock(catalogue);
    } catch (error) {
      console.error('❌ [ProduitModal] Erreur chargement catalogue produits stock:', error);
      setProduitsCatalogueStock([]);
    } finally {
      setLoadingProduitsCatalogueStock(false);
    }
  };

  // Charger les plateaux (produits PAR_PERSONNE) du commerçant
  const loadPlateauxDisponibles = async () => {
    if (!commercantId) return;
    
    try {
      setLoadingPlateaux(true);
      // Récupérer tous les produits du commerçant
      const produits = await apiService.getProduitsByCommercant(commercantId);
      const produitsList = Array.isArray(produits) ? produits : (produits?.produits || []);
      
      // Filtrer uniquement les produits PAR_PERSONNE (plateaux) et exclure le produit actuel
      const plateaux = produitsList.filter(p => 
        p.productType === 'PAR_PERSONNE' && 
        p.actif && 
        (!produit?.id || p.id !== produit.id) // Exclure le produit actuel si on est en édition
      );
      setPlateauxDisponibles(plateaux);
      
      // Si on édite un produit existant, charger les plateaux où il est déjà disponible
      if (produit?.id) {
        try {
          // Requête inverse : trouver les plateaux qui ont ce produit comme ingrédient
          // via ProduitIngredientPlanche où ingredientId = produit.id
          // On charge chaque plateau avec ses ingredientsPlanche pour vérifier
          const plateauxAvecRelations = await Promise.all(
            plateaux.map(async (plateau) => {
              try {
                const response = await apiService.getProduit(plateau.id);
                // L'API renvoie { produit: {...} } ou directement le produit
                const plateauComplet = response.produit || response;
                console.log(`📦 [FRONTEND] Plateau "${plateau.nom}" chargé:`, {
                  id: plateau.id,
                  responseKeys: Object.keys(response),
                  hasProduit: !!response.produit,
                  ingredientsPlanche: plateauComplet.ingredientsPlanche,
                  ingredientsPlancheLength: plateauComplet.ingredientsPlanche?.length || 0,
                  firstIngredient: plateauComplet.ingredientsPlanche?.[0],
                  firstIngredientKeys: plateauComplet.ingredientsPlanche?.[0] ? Object.keys(plateauComplet.ingredientsPlanche[0]) : null
                });
                return {
                  ...plateau,
                  ingredientsPlanche: plateauComplet.ingredientsPlanche || []
                };
              } catch (err) {
                console.error(`❌ [FRONTEND] Erreur chargement plateau ${plateau.id}:`, err);
                return plateau;
              }
            })
          );
          
          // Filtrer les plateaux qui ont ce produit dans ingredientsPlanche
          const plateauxAvecCeProduit = plateauxAvecRelations.filter(plateau => {
            const hasProduct = plateau.ingredientsPlanche?.some(ing => {
              const ingredientId = ing?.ingredientProduit?.id || ing?.ingredientId || ing?.id;
              const match = ingredientId === produit.id;
              if (match) {
                console.log(`✅ Plateau "${plateau.nom}" contient le produit ${produit.id}`);
              }
              return match;
            });
            return hasProduct;
          });
          
          const plateauxIds = plateauxAvecCeProduit.map(p => p.id);
          console.log('🍽️ Plateaux où ce produit est disponible:', plateauxIds);
          setPlateauxIdsDisponibles(plateauxIds);
        } catch (error) {
          console.error('Erreur lors du chargement des plateaux disponibles:', error);
          // En cas d'erreur, on laisse vide et l'utilisateur pourra cocher manuellement
          setPlateauxIdsDisponibles([]);
        }
      } else {
        setPlateauxIdsDisponibles([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des plateaux:', error);
      setPlateauxDisponibles([]);
    } finally {
      setLoadingPlateaux(false);
    }
  };

  const loadElementsComposables = async () => {
    try {
      setLoadingElements(true);
      const token =
        (typeof apiService.getAccessToken === 'function'
          ? apiService.getAccessToken()
          : null) || localStorage.getItem('admin_token');

      const response = await fetch(
        `${API_BASE_URL}/elements-composables?produitId=${produit.id}`,
        {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setElementsComposables(Array.isArray(data) ? data : []);
      } else if (response.status === 404) {
        setElementsComposables([]);
      }
    } catch (error) {
      // Ignorer les erreurs réseau silencieusement si l'endpoint n'existe pas
      if (!error.message.includes('fetch')) {
        console.error('Erreur lors du chargement des éléments composables:', error);
      }
      setElementsComposables([]);
    } finally {
      setLoadingElements(false);
    }
  };

  const loadTaillesPlateaux = async () => {
    if (!produit?.id) {
      setTaillesPlateaux([]);
      return;
    }
    try {
      setLoadingTailles(true);
      const data = await apiService.request(
        `/tailles-plateaux?produitId=${produit.id}`,
        { method: 'GET' }
      );
      setTaillesPlateaux(Array.isArray(data) ? data : []);
    } catch (error) {
      // 404 => aucune taille encore créée pour ce produit => on ignore
      if (error?.status === 404) {
        setTaillesPlateaux([]);
      } else if (!error.message?.includes('fetch')) {
        console.error('Erreur lors du chargement des tailles de plateaux:', error);
      }
    } finally {
      setLoadingTailles(false);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await fetch('/api/templates', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else if (response.status === 404) {
        // Endpoint non implémenté, ignorer silencieusement
        setTemplates([]);
      }
    } catch (error) {
      // Ignorer les erreurs réseau silencieusement si l'endpoint n'existe pas
      if (!error.message.includes('fetch')) {
        console.error('Erreur lors du chargement des templates:', error);
      }
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      await handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      // IMAGES-STANDARD: Upload vers Cloudinary uniquement (plus de base64)
      const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
      
      if (imageFiles.length === 0) {
        alert('Aucune image valide sélectionnée');
        setUploading(false);
        return;
      }

      // Upload vers Cloudinary (retourne uniquement les URLs)
      const uploadedUrls = await uploadMultipleImagesToCloudinary(imageFiles, 'products');
      
      // Ajouter les URLs au state
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Erreur upload images:', error);
      alert('Erreur lors de l\'upload des images. Veuillez réessayer.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const moveImage = (fromIndex, toIndex) => {
    const newImages = [...formData.images];
    const [moved] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, moved);
    setFormData(prev => ({ ...prev, images: newImages }));
  };


  // Gestion des éléments composables
  const addElementComposable = () => {
    setElementsComposables([...elementsComposables, {
      id: `temp-${Date.now()}`,
      nom: '',
      description: '',
      prix: 0,
      categorie: '',
      limiteMax: null,
      ordre: elementsComposables.length,
      actif: true
    }]);
  };

  const updateElementComposable = (index, field, value) => {
    const newElements = [...elementsComposables];
    newElements[index] = { ...newElements[index], [field]: value };
    setElementsComposables(newElements);
  };

  const removeElementComposable = (index) => {
    setElementsComposables(elementsComposables.filter((_, i) => i !== index));
  };

  // Gestion des tailles de plateaux
  const addTaillePlateau = () => {
    setTaillesPlateaux([...taillesPlateaux, {
      id: `temp-${Date.now()}`,
      nom: '',
      nombrePieces: 8,
      prix: null,
      prixParPiece: null,
      limitesParCategorie: null,
      actif: true,
      ordre: taillesPlateaux.length
    }]);
  };

  const updateTaillePlateau = (index, field, value) => {
    const newTailles = [...taillesPlateaux];
    newTailles[index] = { ...newTailles[index], [field]: value };
    setTaillesPlateaux(newTailles);
  };

  const removeTaillePlateau = (index) => {
    setTaillesPlateaux(taillesPlateaux.filter((_, i) => i !== index));
  };

  const saveTaillesPlateaux = async (produitId) => {
    try {
      // Supprimer toutes les tailles existantes pour ce produit (celles déjà en BDD)
      const existingTailles = taillesPlateaux.filter(
        (t) => t.id && !String(t.id).startsWith('temp')
      );
      for (const taille of existingTailles) {
        try {
          await apiService.request(`/tailles-plateaux/${taille.id}`, {
            method: 'DELETE',
          });
        } catch (error) {
          // On log mais on ne bloque pas la suite pour une taille isolée
          console.error('Erreur lors de la suppression de la taille:', error);
        }
      }

      // Créer les nouvelles tailles (celles saisies dans le modal)
      const taillesToSave = taillesPlateaux.filter(
        (t) => t.nom && Number(t.nombrePieces) > 0
      );

      if (taillesToSave.length > 0) {
        await apiService.request('/tailles-plateaux/bulk', {
          method: 'POST',
          body: JSON.stringify({
            produitId,
            tailles: taillesToSave.map((t) => ({
              nom: t.nom,
              nombrePieces: parseInt(t.nombrePieces, 10),
              prix:
                t.prix !== null && t.prix !== ''
                  ? parseFloat(String(t.prix).replace(',', '.'))
                  : null,
              prixParPiece:
                t.prixParPiece !== null && t.prixParPiece !== ''
                  ? parseFloat(String(t.prixParPiece).replace(',', '.'))
                  : null,
              limitesParCategorie: t.limitesParCategorie || null,
              actif: t.actif !== undefined ? t.actif : true,
              ordre:
                t.ordre !== undefined && t.ordre !== null
                  ? parseInt(t.ordre, 10)
                  : 0,
            })),
          }),
        });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des tailles de plateaux:', error);
      // On remonte une erreur lisible pour le dialog global
      const message =
        error?.message || 'Erreur lors de la sauvegarde des tailles';
      throw new Error(message);
    }
  };

  const saveElementsComposables = async (produitId) => {
    try {
      // On enregistre tous les éléments ayant au moins un nom.
      // Le prix peut être à 0 (élément inclus dans le plateau).
      const elementsToSave = elementsComposables.filter((el) => el.nom);

      if (elementsToSave.length > 0) {
        // Utiliser le même système de tokens que le reste de l'admin
        const token =
          (typeof apiService.getAccessToken === 'function'
            ? apiService.getAccessToken()
            : null) || localStorage.getItem('admin_token');

        const response = await fetch(`${API_BASE_URL}/elements-composables/bulk`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          },
          body: JSON.stringify({
            produitId,
            elements: elementsToSave
          })
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la sauvegarde des éléments composables');
        }
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des éléments composables:', error);
      throw error;
    }
  };

  // Gestion des templates
  const applyTemplate = async (template) => {
    try {
      const response = await fetch(`/api/templates/${template.id}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ produitId: produit?.id })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Appliquer les options du template
        setOptions(data.options || []);
        setContraintes(data.contraintes || []);
        alert('Template appliqué avec succès !');
      }
    } catch (error) {
      console.error('Erreur lors de l\'application du template:', error);
      alert('Erreur lors de l\'application du template');
    }
  };

  // Gestion des contraintes
  const addContrainte = () => {
    setContraintes([...contraintes, {
      id: `temp-${Date.now()}`,
      type: 'QUANTITY',
      nom: '',
      description: '',
      valeur: '',
      message: '',
      active: true
    }]);
  };

  const updateContrainte = (index, field, value) => {
    const newContraintes = [...contraintes];
    newContraintes[index] = { ...newContraintes[index], [field]: value };
    setContraintes(newContraintes);
  };

  const removeContrainte = (index) => {
    setContraintes(contraintes.filter((_, i) => i !== index));
  };

  const toggleIngredientSelection = (ingredientId) => {
    setSelectedIngredientIds((prev) =>
      prev.includes(ingredientId)
        ? prev.filter((id) => id !== ingredientId)
        : [...prev, ingredientId]
    );
  };

  const filteredIngredients = useMemo(() => {
    const query = ingredientsSearch.trim().toLowerCase();
    let baseList = ingredientsCatalog;
    
    // ⭐ Inclure les ingrédients sélectionnés même s'ils ne sont pas dans le catalogue
    // (cas où un ingrédient a été créé par un autre commerçant ou est global)
    if (selectedIngredientIds.length > 0) {
      const selectedNotInCatalog = selectedIngredientIds
        .filter(id => !ingredientsCatalog.some(ing => ing.id === id))
        .map(id => ({
          id,
          nom: `Ingrédient ${id.substring(0, 8)}...`, // Placeholder si pas trouvé
          actif: true,
          categorie: null
        }));
      baseList = [...ingredientsCatalog, ...selectedNotInCatalog];
    }
    
    if (!query) return baseList;
    return baseList.filter((ingredient) => {
      const nom = ingredient.nom || '';
      const categorie = ingredient.categorie || '';
      return (
        nom.toLowerCase().includes(query) ||
        categorie.toLowerCase().includes(query)
      );
    });
  }, [ingredientsCatalog, ingredientsSearch, selectedIngredientIds]);

  const selectedIngredientNames = useMemo(
    () =>
      ingredientsCatalog
        .filter((ingredient) => selectedIngredientIds.includes(ingredient.id))
        .map((ingredient) => ingredient.nom)
        .filter(Boolean),
    [ingredientsCatalog, selectedIngredientIds]
  );

  // Créer un nouvel ingrédient
  const handleCreateIngredient = async () => {
    if (!newIngredientData.nom.trim()) {
      setIngredientNameError('Indiquez le nom de l\'ingrédient.');
      return;
    }
    setIngredientNameError('');

    try {
      setCreatingIngredient(true);
      const allergenesArray = newIngredientAllergenes
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

      const payload = {
        nom: newIngredientData.nom.trim(),
        description: newIngredientData.description?.trim() || null,
        allergenes: allergenesArray,
        actif: true,
        prix: 0,
        commercantId: commercantId || null  // ⭐ AJOUTER commercantId
      };

      const newIngredient = await apiService.request('/ingredients', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Recharger le catalogue et sélectionner le nouvel ingrédient
      await loadIngredientsCatalog();
      setSelectedIngredientIds((prev) => [...prev, newIngredient.id]);
      
      // Si on est en mode planche par personne, ajouter aussi à ingredientIdsPlanche
      if (formData.isPlateauParPersonne) {
        setFormData(prev => ({
          ...prev,
          ingredientIdsPlanche: [...prev.ingredientIdsPlanche, newIngredient.id]
        }));
      }
      
      // Réinitialiser le formulaire
      setNewIngredientData({ nom: '', description: '', allergenes: [] });
      setNewIngredientAllergenes('');
      setShowCreateIngredientModal(false);
    } catch (error) {
      console.error('Erreur lors de la création de l\'ingrédient:', error);
      alert(error.message || 'Erreur lors de la création de l\'ingrédient');
    } finally {
      setCreatingIngredient(false);
    }
  };

  const handleEnableReservationFromService = async () => {
    if (!produit?.id || convertingToReservable) return;
    const nom = String(formData.nom || produit.nom || '').trim();
    const prixRaw = formData.prix ?? produit.prix;
    const prix = parseFloat(String(prixRaw).replace(',', '.'));
    if (!nom || !Number.isFinite(prix) || prix <= 0) {
      setErrorDialog({
        open: true,
        title: 'Informations incomplètes',
        message: 'Nom et prix valides sont requis avant d’activer la réservation.',
        raw: null,
      });
      return;
    }
    setConvertingToReservable(true);
    try {
      const payload = buildServiceToPrestationConversionPayload(
        { formData, produit },
        {
          commercantId,
          commercantType,
          normalizeTauxTVA: normalizeTauxTVAInput,
        }
      );
      if (!payload.nom || payload.prix == null || payload.prix <= 0) {
        setErrorDialog({
          open: true,
          title: 'Informations incomplètes',
          message: 'Nom et prix valides sont requis avant d’activer la réservation.',
          raw: null,
        });
        return;
      }
      const created = await apiService.createPrestation(payload);
      // Idempotent : déjà converti → on désactive quand même le SERVICE legacy
      if (produit.actif !== false) {
        await apiService.updateProduit(produit.id, { actif: false });
      }
      const prestationId = created?.prestation?.id || created?.id || null;
      if (onSave) {
        await onSave({
          ...produit,
          actif: false,
          _convertedPrestationId: prestationId,
          _alreadyConverted: Boolean(created?.alreadyConverted),
        });
      }
      onClose();
    } catch (err) {
      setErrorDialog({
        open: true,
        title: 'Activation impossible',
        message: err?.message || 'Impossible de créer la prestation réservable.',
        raw: err,
      });
    } finally {
      setConvertingToReservable(false);
    }
  };

  const handleSubmit = async () => {
    if (saving || savingLockRef.current) {
      return;
    }

    const blockingIssues = getProduitFormBlockingIssues(formData, taillesPlateaux);
    if (blockingIssues.length > 0) {
      scrollToProduitSection(blockingIssues[0].sectionKey);
      return;
    }

    savingLockRef.current = true;
    setSaving(true);
    setCodeBarresError('');
    try {
    // Conversion des prix (gérer virgule vs point)
    const convertPrice = (priceStr) => {
      if (!priceStr) return null;
      const cleaned = String(priceStr).replace(',', '.').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    // ——— SERVICE catalogue : payload whitelist (préserve stock/ingrédients/food hors UI)
    if (isServiceTypeProduit(formData.typeProduit)) {
      const dataToSave = produit
        ? buildServiceProduitUpdatePayload({
            formData,
            baselineProduit: produit,
            commercantId,
            convertPrice,
            normalizeTauxTVA: normalizeTauxTVAInput,
            commercantType,
            includeAdvanced: true,
          })
        : buildServiceProduitCreatePayload({
            formData,
            commercantId,
            convertPrice,
            normalizeTauxTVA: normalizeTauxTVAInput,
            commercantType,
          });

      let savedProduit;
      if (produit) {
        savedProduit = await apiService.updateProduit(produit.id, dataToSave);
      } else {
        if (!createIdempotencyKeyRef.current) {
          createIdempotencyKeyRef.current =
            (typeof crypto !== 'undefined' && crypto.randomUUID)
              ? crypto.randomUUID()
              : `prod-create-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        savedProduit = await apiService.createProduit(dataToSave, {
          idempotencyKey: createIdempotencyKeyRef.current,
        });
      }

      try {
        const produitReloaded = await apiService.request(
          `/produits/${savedProduit.id || produit.id}`
        );
        onSave(produitReloaded?.produit || savedProduit);
      } catch {
        onSave(savedProduit);
      }
      onClose();
      createIdempotencyKeyRef.current = null;
      return;
    }

    // ——— PHYSIQUE : chemin historique inchangé
    // Les ingrédients affichés au client sont automatiquement ceux sélectionnés
    const combinedIngredientsText = selectedIngredientNames;

    // Exclure les champs frontend uniquement qui ne doivent pas être envoyés au backend
    const { isPlateauParPersonne, prixProHt: prixProHtRaw, ...formDataForBackend } = formData;
    // Booléens dérivés des caractéristiques : ne jamais renvoyer une valeur locale concurrente.
    const formDataWithoutDerivedBools = omitLegacyBoolFields(formDataForBackend);
    
    const dataToSave = {
        ...formDataWithoutDerivedBools,
        commercantId,
        prix: cataloguePrixSentOnSave(formData.venteAuPoids, convertPrice(formData.prix)), // Prix = 0 si vente au poids
        codeBarres: formData.codeBarres?.trim() ? formData.codeBarres.trim() : null,
        referenceInterne: formData.referenceInterne?.trim() ? formData.referenceInterne.trim() : null,
        prixPromo: formData.prixPromo ? convertPrice(formData.prixPromo) : null,
        ...buildPrixProHtSaveField(moduleB2bEnabled, prixProHtRaw, convertPrice),
        stock: formData.stockLimite ? parseInt(formData.stock) : -1,
        tempsPreparationPersonnalise: Boolean(formData.tempsPreparationPersonnalise),
        tempsPreparation: formData.tempsPreparationPersonnalise
          ? (parseInt(formData.tempsPreparation, 10) || 15)
          : (parseInt(formData.tempsPreparation, 10) || 15),
        modeComposition: formData.modeComposition,
        incrementPieces: formData.modeComposition ? parseInt(formData.incrementPieces) : 1,
        limiteTotal: formData.modeComposition && formData.limiteTotal ? parseInt(formData.limiteTotal) : null,
        validationMode: formData.modeComposition ? formData.validationMode : 'FLEXIBLE',
      tauxTVA: normalizeTauxTVAInput(formData.tauxTVA, commercantType),
      // Vente au poids
      venteAuPoids: formData.venteAuPoids || false,
      prixAuPoids: formData.venteAuPoids ? (convertPrice(formData.prixAuPoids) || 0) : null,
      unitePoids: formData.venteAuPoids ? (formData.unitePoids || 'kg') : 'kg',
      poidsPortionStandard: formData.venteAuPoids && formData.poidsPortionStandard ? parseFloat(formData.poidsPortionStandard) : null,
      nombreTranchesParPortion: formData.venteAuPoids && formData.nombreTranchesParPortion ? parseInt(formData.nombreTranchesParPortion) : null,
      decoupesDisponibles: formData.venteAuPoids ? (formData.decoupesDisponibles || []) : [],
      options: serializeProduitOptionsForSave(options),
      contraintes: contraintes.filter(c => c.nom && c.description),
      ingredientIds: selectedIngredientIds,
      ingredientsText: combinedIngredientsText,
      ingredients: combinedIngredientsText,
      // Type détaillé du produit (optionnel, NULL pour rétrocompatibilité)
      // Mapping simplifié : isPlateauParPersonne → productType = 'PAR_PERSONNE'
      productType: formData.isPlateauParPersonne ? 'PAR_PERSONNE' : null,
      minPersons: formData.isPlateauParPersonne ? (formData.minPersons || null) : null,
      maxPersons: formData.isPlateauParPersonne ? (formData.maxPersons || null) : null,
      prixParPersonne: formData.isPlateauParPersonne ? (formData.prixParPersonne ? parseFloat(formData.prixParPersonne) : null) : null,
      // ⚠️ IMPORTANT : Utiliser la nouvelle relation ProduitIngredientPlanche (séparée de ProduitIngredient)
      // Cette relation ne déclenche PAS la désactivation automatique en cas de rupture de stock
      ingredientIdsPlanche: formData.isPlateauParPersonne && formData.ingredientIdsPlanche.length > 0
        ? formData.ingredientIdsPlanche
        : undefined,
      // Plateaux où ce produit (ingrédient) est disponible
      // On utilise la même relation ProduitIngredientPlanche mais dans l'autre sens :
      // - Pour un plateau : ingredientIdsPlanche = liste des ingrédients
      // - Pour un ingrédient : plateauxIdsDisponibles = liste des plateaux où il est disponible
      // IMPORTANT : On envoie toujours le tableau (même vide) pour permettre la suppression des relations
      plateauxIdsDisponibles: !formData.isPlateauParPersonne
        ? plateauxIdsDisponibles
        : undefined,
      // preparationRequis n'est plus utilisé (services gérés via Prestation)
      preparationRequis: null,
      // A2.0 Expédition — PHYSIQUE only
      shippingProfileId: formData.shippingProfileId || null,
      shippingWeightGrams:
        formData.shippingProfileId && formData.shippingWeightGrams !== ''
          ? parseInt(formData.shippingWeightGrams, 10)
          : null,
      };

      console.log('🍽️ Plateaux disponibles à sauvegarder:', {
        isPlateauParPersonne: formData.isPlateauParPersonne,
        plateauxIdsDisponibles: plateauxIdsDisponibles,
        plateauxIdsDisponiblesLength: plateauxIdsDisponibles.length,
        willBeSent: !formData.isPlateauParPersonne ? plateauxIdsDisponibles : undefined,
        inDataToSave: dataToSave.plateauxIdsDisponibles
      });
      
      let savedProduit;
      if (produit) {
        savedProduit = await apiService.updateProduit(produit.id, dataToSave);
      } else {
        if (!createIdempotencyKeyRef.current) {
          createIdempotencyKeyRef.current =
            (typeof crypto !== 'undefined' && crypto.randomUUID)
              ? crypto.randomUUID()
              : `prod-create-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        }
        savedProduit = await apiService.createProduit(dataToSave, {
          idempotencyKey: createIdempotencyKeyRef.current,
        });
      }
      
      console.log('✅ Produit sauvegardé:', savedProduit);
      console.log('📋 Ingrédients envoyés:', selectedIngredientIds);
      console.log('📋 Ingrédients reçus:', savedProduit?.ingredients?.map(i => i.ingredientId || i.ingredient?.id || i.id) || []);

      // Sauvegarder les éléments composables et tailles si le mode composition est activé
      if (formData.modeComposition) {
        if (elementsComposables.length > 0) {
          await saveElementsComposables(savedProduit.id || produit.id);
        }
        if (taillesPlateaux.length > 0) {
          await saveTaillesPlateaux(savedProduit.id || produit.id);
        }
      }

      // Recharger le produit complet depuis l'API pour avoir les ingrédients à jour
      try {
        const produitReloaded = await apiService.request(`/produits/${savedProduit.id || produit.id}`);
        if (produitReloaded?.produit) {
          console.log('🔄 Produit rechargé avec ingrédients:', produitReloaded.produit.ingredients?.length || 0, 'ingrédients');
          console.log('📋 Détails ingrédients rechargés:', produitReloaded.produit.ingredients);
          console.log('🍽️ Produit rechargé avec ingredientsPlanche:', produitReloaded.produit.ingredientsPlanche?.length || 0, 'ingrédients planche');
          console.log('📋 Détails ingredientsPlanche rechargés:', produitReloaded.produit.ingredientsPlanche);
          
          // Recharger les plateaux disponibles pour mettre à jour la sélection
          if (!formData.isPlateauParPersonne) {
            await loadPlateauxDisponibles();
          }
          
          onSave(produitReloaded.produit);
        } else {
          console.warn('⚠️ Produit rechargé sans structure {produit}');
          onSave(savedProduit);
        }
      } catch (reloadError) {
        console.error('⚠️ Erreur lors du rechargement du produit:', reloadError);
        onSave(savedProduit);
      }
      
      onClose();
      createIdempotencyKeyRef.current = null;
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      // Extraire les détails de validation renvoyés par le backend (validateInput)
      const apiRawDetails =
        error?.data?.details ||
        error?.response?.data?.details ||
        null;

      const apiErrorCode =
        error?.code ||
        error?.data?.code ||
        error?.data?.error?.code ||
        undefined;

      let errorMessage =
        error?.data?.message ||
        error?.message ||
        error?.error?.message ||
        (typeof error?.data?.error === 'string' ? error.data.error : null) ||
        error?.data?.error?.message ||
        'Erreur lors de la sauvegarde du produit';

      // Cas 1 : tableau de détails (validation Joi)
      if (Array.isArray(apiRawDetails) && apiRawDetails.length > 0) {
        const formattedDetails = apiRawDetails
          .map((d) => `• ${d.field || 'champ inconnu'} : ${d.message}`)
          .join('\n');
        errorMessage += `\n\nDétails :\n${formattedDetails}`;
      }

      // Cas 2 : détail brut sous forme de chaîne (erreur métier/Prisma)
      if (typeof apiRawDetails === 'string' && apiRawDetails.trim().length > 0) {
        errorMessage += `\n\nDétail technique :\n${apiRawDetails}`;
      }

      console.error('❌ Détails complets erreur produit:', {
        message: errorMessage,
        raw: error
      });

      if (apiErrorCode === 'BARCODE_INVALID' || apiErrorCode === 'BARCODE_DUPLICATE') {
        setCodeBarresError(errorMessage);
      }

      setErrorDialog({
        open: true,
        title:
          apiErrorCode === 'BARCODE_INVALID'
            ? 'Code-barres invalide'
            : apiErrorCode === 'BARCODE_DUPLICATE'
              ? 'Code-barres déjà utilisé'
              : 'Impossible d\'enregistrer le produit',
        message: errorMessage,
        raw: error
      });
    } finally {
      savingLockRef.current = false;
      setSaving(false);
    }
  };

  // Fonctions simples pour les options
  const addOption = () => {
    const nouvelleOption = {
      id: `temp-${crypto.randomUUID()}`,
      nom: '',
      type: 'CHOIX_UNIQUE',
      choixMinimum: 0,
      choixMaximum: 1,
      nbInclus: 0,
      maxIllimite: false,
      obligatoire: false,
      choix: []
    };
    setOptions([...options, nouvelleOption]);
  };

  const updateOption = (index, field, value) => {
    const newOptions = [...options];
    newOptions[index][field] = value;
    if (field === 'type') {
      newOptions[index].choixMultiple = value === 'CHOIX_MULTIPLE';
      if (value === 'CHOIX_MULTIPLE') {
        newOptions[index].choixMaximum = newOptions[index].maxIllimite ? null : (newOptions[index].choixMaximum ?? null);
        if (newOptions[index].choixMaximum === 1) {
          newOptions[index].choixMaximum = null;
          newOptions[index].maxIllimite = true;
        }
      } else {
        newOptions[index].choixMaximum = 1;
        newOptions[index].maxIllimite = false;
        newOptions[index].nbInclus = 0;
      }
    }
    if (field === 'obligatoire' && value) {
      const min = Math.max(0, parseInt(newOptions[index].choixMinimum, 10) || 0);
      if (min < 1) {
        newOptions[index].choixMinimum = 1;
      }
    }
    if (field === 'maxIllimite') {
      newOptions[index].maxIllimite = Boolean(value);
      newOptions[index].choixMaximum = value ? null : (newOptions[index].choixMaximum ?? 2);
    }
    setOptions(newOptions);
  };

  const removeOption = (index) => {
    const newOptions = options.filter((_, i) => i !== index);
    setOptions(newOptions);
  };

  const addChoixToOption = (optionIndex) => {
    const newOptions = [...options];
    const nouveauChoix = {
      id: `temp-${crypto.randomUUID()}`,
      nom: '',
      prix: 0,
      linkedProduitId: null,
    };
    newOptions[optionIndex].choix.push(nouveauChoix);
    setOptions(newOptions);
  };

  const updateChoix = (optionIndex, choixIndex, field, value) => {
    const newOptions = [...options];
    newOptions[optionIndex].choix[choixIndex][field] = value;
    setOptions(newOptions);
  };

  const removeChoix = (optionIndex, choixIndex) => {
    const newOptions = [...options];
    newOptions[optionIndex].choix = newOptions[optionIndex].choix.filter((_, i) => i !== choixIndex);
    setOptions(newOptions);
  };

  if (!isOpen) return null;

  // Textes conditionnels selon le type de produit
  const isNonFood = formData.productType === 'COMMERCE_NON_FOOD';
  const typeProduitHelpText = isNonFood
    ? 'Vêtement, accessoire, fleur, objet déco...'
    : 'Sandwich, t-shirt, objet...';
  const nomProduitPlaceholder = isNonFood
    ? 'Ex : Bouquet de fleurs, bougie parfumée, pull en laine'
    : 'Ex: California Roll avec saumon frais';
  const descriptionPlaceholder = isNonFood
    ? 'Décrivez clairement votre produit (matière, style, utilisation, etc.)'
    : 'Décrivez votre produit de manière appétissante...';

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        {/* Modal */}
        <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
            <div className="min-w-0">
              <h3 className="text-xl font-semibold text-white">
                {getProduitModalTitle({
                  isService,
                  isEdit: Boolean(produit),
                })}
              </h3>
              <p className="mt-1 text-xs text-white/85" data-offre-locked-type>
                {getOffreLockedTypeLabel({ isService })}
              </p>
            </div>
            <button onClick={onClose} className="p-2 text-white hover:bg-white/20 rounded-lg transition">
              <X className="h-5 w-5" />
            </button>
          </div>

          {!isService && (
            <ProduitArchetypeBanner
              archetypeId={archetypeId}
              enrichExpanded={enrichExpanded}
              onToggleEnrich={() => setEnrichExpanded((v) => !v)}
              revealedSections={revealedSections}
              onRevealSection={handleRevealSection}
              onRequestChangeArchetype={handleChangeArchetypeRequest}
            />
          )}

          {/* Content */}
          <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
            <div className="space-y-6">
              {/* 1. Photos */}
              {showSecForType('photos') && (
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <ProduitSectionHeader
                  title={isService ? 'Photos' : 'Photos du produit'}
                  helpKey={SK.PHOTOS}
                />
                <p className="text-xs text-gray-500 -mt-2 mb-3">Glissez vos photos ici — la première est la photo principale.</p>
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 transition ${
                    dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileInput}
                    className="hidden"
                    id="file-upload"
                  />
                  
                  {formData.images.length === 0 ? (
                    <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                      <Upload className="h-12 w-12 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Cliquez ou glissez vos photos ici</p>
                      <p className="text-xs text-gray-400 mt-1">PNG, JPG jusqu'à 10MB</p>
                    </label>
                  ) : (
                    <div className="grid grid-cols-4 gap-4">
                      {formData.images.map((img, index) => (
                        <div key={index} className="relative group">
                          <img src={img} alt={`Photo ${index + 1}`} className="w-full h-32 object-cover rounded-lg" />
                          {index === 0 && (
                            <span className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded">
                              Principale
                            </span>
                          )}
                          <button
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition"
                          >
                            <X className="h-4 w-4" />
                          </button>
                          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1 opacity-0 group-hover:opacity-100 transition">
                            {index > 0 && (
                              <button
                                onClick={() => moveImage(index, index - 1)}
                                className="bg-white p-1 rounded shadow"
                              >
                                ←
                              </button>
                            )}
                            {index < formData.images.length - 1 && (
                              <button
                                onClick={() => moveImage(index, index + 1)}
                                className="bg-white p-1 rounded shadow"
                              >
                                →
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      <label
                        htmlFor="file-upload"
                        className="flex items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 hover:bg-primary-50 transition"
                      >
                        <Plus className="h-8 w-8 text-gray-400" />
                      </label>
                    </div>
                  )}
                  
                  {uploading && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="text-sm text-gray-600 mt-2">Upload en cours...</p>
                      </div>
                    </div>
                  )}
                </div>
                {formData.images.length > 0 && formData.images.length < 2 && (
                  <div className="mt-2 flex items-start space-x-2 text-yellow-700 bg-yellow-50 p-3 rounded-lg">
                    <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                      <strong>Conseil :</strong> Ajoutez au moins 2 photos pour attirer plus de clients
                    </p>
                  </div>
                )}
              </div>
              )}

              {/* Note : SERVICE catalogue = formulaire contextuel ci-dessous ; RDV = onglet Mes Prestations */}

              {/* 3. Informations de base */}
              {showSecForType('infos') && (
              <div data-produit-section="infos" className="rounded-xl border border-gray-200 bg-white p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <ProduitSectionHeader
                    title={isService ? 'Nom de la prestation *' : 'Nom du produit *'}
                    helpKey={SK.INFOS}
                  />
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${formFieldErrorClass(PRODUIT_FORM_ISSUE_IDS.NOM)}`}
                    placeholder={isService ? 'Ex. : Aisselles, Massage dos…' : nomProduitPlaceholder}
                  />
                  {hasFormBlocker(PRODUIT_FORM_ISSUE_IDS.NOM) && (
                    <p className="text-xs text-red-600 mt-1">
                      {isService ? 'Indiquez le nom de la prestation.' : 'Indiquez le nom du produit.'}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder={
                      isService
                        ? 'Décrivez le service pour vos clients…'
                        : descriptionPlaceholder
                    }
                  />
                </div>

                {shouldMountPrixTarificationBlock(showSecForType('prix'), formData.venteAuPoids) && (
                  <div data-produit-section="prix" className="md:col-span-2 space-y-4">
                    <ProduitSectionHeader title="Prix et tarification" helpKey={SK.PRIX} />
                    <ProduitIdentificationBarcodes
                      produitId={produit?.id}
                      produitNom={formData.nom}
                      prix={formData.prix}
                      prixPromo={formData.prixPromo}
                      codeBarres={formData.codeBarres}
                      referenceInterne={formData.referenceInterne}
                      onReferenceInterneChange={(v) => setFormData({ ...formData, referenceInterne: v })}
                      internalBarcode={internalBarcode}
                      codeBarresError={codeBarresError}
                      onCodeBarresChange={(v) => {
                        setCodeBarresError('');
                        setFormData({ ...formData, codeBarres: v });
                      }}
                      onGenerateInternal={async () => {
                        if (!produit?.id) return;
                        const res = await apiService.generateProduitInternalBarcode(produit.id);
                        if (res?.internalBarcode) setInternalBarcode(res.internalBarcode);
                      }}
                      productOptions={options || []}
                      choixInternalById={choixInternalById}
                      onGenerateChoixInternal={async (choixId) => {
                        const res = await apiService.generateChoixInternalBarcode(choixId);
                        if (res?.internalBarcode) {
                          setChoixInternalById((prev) => ({
                            ...prev,
                            [choixId]: res.internalBarcode,
                          }));
                        }
                      }}
                    />
                    <div className="space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className={`flex items-center min-h-[1.75rem] text-sm font-medium ${areClassicPriceInputsInactive(formData.venteAuPoids) ? 'text-gray-500' : 'text-gray-900'}`}>
                          Prix de base * (€)
                        </label>
                        <ProduitSectionHeader
                          title="Prix promo"
                          helpKey="prixPromo"
                          optional
                          variant="field"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={formData.prix}
                              onChange={(e) => setFormData({ ...formData, prix: e.target.value })}
                              disabled={areClassicPriceInputsInactive(formData.venteAuPoids)}
                            className={classicPriceInputClassName(
                              formData.venteAuPoids,
                              `w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${formFieldErrorClass(PRODUIT_FORM_ISSUE_IDS.PRIX, PRODUIT_FORM_ISSUE_IDS.PRIX_PLATEAU_OU_CATALOGUE)}`
                            )}
                            placeholder="15.50"
                            required={!areClassicPriceInputsInactive(formData.venteAuPoids)}
                          />
                        </div>
                        {(hasFormBlocker(PRODUIT_FORM_ISSUE_IDS.PRIX) ||
                          hasFormBlocker(PRODUIT_FORM_ISSUE_IDS.PRIX_PLATEAU_OU_CATALOGUE)) && (
                          <p className="text-xs text-red-600 mt-1">
                            {formBlockingIssues.find(
                              (i) =>
                                i.id === PRODUIT_FORM_ISSUE_IDS.PRIX ||
                                i.id === PRODUIT_FORM_ISSUE_IDS.PRIX_PLATEAU_OU_CATALOGUE
                            )?.message}
                          </p>
                        )}
                        {formData.prix && parseFloat(formData.prix) % 1 !== 0 && parseFloat(formData.prix) % 0.5 !== 0 && (
                            <p className="text-xs text-gray-500 mt-1">
                              💡 Conseil : un prix rond (15 € ou 15,50 €) est plus lisible
                            </p>
                          )}
                        </div>
                        <div className="relative">
                          <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="number"
                            step="0.01"
                            value={formData.prixPromo}
                            onChange={(e) => setFormData({ ...formData, prixPromo: e.target.value })}
                            disabled={areClassicPriceInputsInactive(formData.venteAuPoids)}
                            className={classicPriceInputClassName(
                              formData.venteAuPoids,
                              'w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent'
                            )}
                            placeholder="13.00"
                          />
                        </div>
                      </div>
                      {areClassicPriceInputsInactive(formData.venteAuPoids) && (
                        <p className="text-xs text-gray-500">
                          Non utilisés en vente au poids : le client paie au kilo (ou à l’unité choisie). Ces montants restent mémorisés si vous désactivez le mode.
                        </p>
                      )}
                      {isB2bV1CompatibleProductForm(formData) && (
                        <div>
                          <ProduitSectionHeader title="Tarif PRO HT" />
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={formData.prixProHt}
                              onChange={(e) => setFormData({ ...formData, prixProHt: e.target.value })}
                              disabled={!moduleB2bEnabled}
                              className="w-full pl-10 pr-16 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                              placeholder="80.00"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500">€ HT</span>
                          </div>
                          {!moduleB2bEnabled && (
                            <p className="text-xs text-amber-700 mt-1">
                              Module B2B non activé — contactez Clickorico
                            </p>
                          )}
                          {moduleB2bEnabled && formData.prixProHt !== '' && previewProTtc(formData.prixProHt, formData.tauxTVA) != null && (
                            <p className="text-sm text-gray-700 mt-2">
                              {Number(parseFloat(String(formData.prixProHt).replace(',', '.'))).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € HT
                              <span className="block text-xs text-gray-500">
                                → {previewProTtc(formData.prixProHt, formData.tauxTVA).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € TTC
                                {' '}(TVA {Number(formData.tauxTVA ?? 20).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %)
                              </span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="max-w-md">
                      <ProduitSectionHeader title="Taux de TVA *" helpKey="tva" />
                      <select
                        value={formData.tauxTVA}
                        onChange={(e) => setFormData({ ...formData, tauxTVA: parseFloat(e.target.value) })}
                        disabled={!isTvaEditableWithWeight(formData.venteAuPoids)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {TVA_RATE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Prix en TTC. Par défaut :{' '}
                        {isRestaurationCategorie(commercantType) ? '5,5 % (restauration)' : '20 % (taux normal)'}.
                      </p>
                    </div>
                    {produit?.id && (
                      <PrixCanalSection produitId={produit.id} prixCatalogue={formData.prix} />
                    )}
                  </div>
                )}

                {showStandaloneTva && !showSecForType('prix') && (
                <div className="md:col-span-2 max-w-md">
                  <ProduitSectionHeader title="Taux de TVA *" helpKey="tva" />
                  <select
                    value={formData.tauxTVA}
                    onChange={(e) => setFormData({ ...formData, tauxTVA: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    {TVA_RATE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                )}

                {showSecForType('planchePersonnes') && formData.typeProduit === 'PHYSIQUE' && (
                  <div className="md:col-span-2 border-t pt-4 mt-2 rounded-lg border border-amber-100 bg-amber-50/30 p-4" data-produit-section="planchePersonnes">
                    <ProduitSectionHeader title="Planche par personnes" helpKey={SK.PLANCHE_PERSONNES} />
                    {(archetypeId === ARCHETYPE_IDS.PLANCHE_PERSONNES ||
                      archetypeId === ARCHETYPE_IDS.PLANCHE_PERSO) && (
                      <p className="text-sm font-medium text-orange-900 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 mb-3">
                        💰 Indiquez le <strong>prix par personne</strong> ci-dessous — c&apos;est le tarif
                        affiché au client pour cette planche.
                      </p>
                    )}
                    {!lockPlancheToggle && (
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.isPlateauParPersonne || false}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData({
                            ...formData,
                            isPlateauParPersonne: checked,
                            // Initialiser les valeurs par défaut si on active
                            minPersons: checked && !formData.minPersons ? 2 : formData.minPersons,
                            maxPersons: checked && !formData.maxPersons ? 20 : formData.maxPersons,
                            prixParPersonne: checked && !formData.prixParPersonne ? formData.prix || null : (checked ? formData.prixParPersonne : null)
                          });
                        }}
                        className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700">
                          C'est un plateau / une planche par personne
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          (Il Piatto, planches tapas, sushi, etc.)
                        </p>
                      </div>
                    </label>
                    )}

                    {(lockPlancheToggle || formData.isPlateauParPersonne) && (
                      <div className="mt-4 space-y-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nombre minimum de personnes *
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={formData.minPersons || 2}
                              onChange={(e) => setFormData({ ...formData, minPersons: parseInt(e.target.value) || 2 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              placeholder="2"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Nombre maximum de personnes *
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={formData.maxPersons || 20}
                              onChange={(e) => setFormData({ ...formData, maxPersons: parseInt(e.target.value) || 20 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              placeholder="20"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Prix par personne (€) *
                            </label>
                            <div className="relative">
                              <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.prixParPersonne || ''}
                                onChange={(e) => setFormData({ ...formData, prixParPersonne: e.target.value ? parseFloat(e.target.value) : null })}
                                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${formFieldErrorClass(PRODUIT_FORM_ISSUE_IDS.PRIX_PAR_PERSONNE)}`}
                                placeholder="9.90"
                              />
                            </div>
                            {hasFormBlocker(PRODUIT_FORM_ISSUE_IDS.PRIX_PAR_PERSONNE) && (
                              <p className="text-xs text-red-600 mt-1">
                                Indiquez un prix par personne pour cette planche.
                              </p>
                            )}
                          </div>
                        </div>
                        
                        {/* ⭐ NOUVEAU: Mode de composition (FIXED ou COMPOSABLE) */}
                        <div className="mt-4 pt-4 border-t border-orange-300">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Mode de planche
                          </label>
                          <select
                            value={formData.compositionMode || ''}
                            onChange={(e) => setFormData({ ...formData, compositionMode: e.target.value || null })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="">Par défaut (Variée + Personnalisée)</option>
                            <option value="FIXED">FIXED - Seulement planche variée (commerçant compose)</option>
                            <option value="COMPOSABLE">COMPOSABLE - Variée + Personnalisée (client peut choisir)</option>
                          </select>
                          <p className="text-xs text-gray-600 mt-2">
                            <strong>FIXED :</strong> Le client ne peut pas personnaliser, vous composez la planche selon ses goûts.<br/>
                            <strong>COMPOSABLE ou par défaut :</strong> Le client peut choisir entre planche variée (vous composez) ou personnalisée (il choisit les ingrédients).
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Section "Ingrédients disponibles pour la planche" (séparée de "Ingrédients & allergènes") */}
                    {(lockPlancheToggle || formData.isPlateauParPersonne) && (
                      <div className="mt-4 p-4 bg-amber-50/50 border border-amber-200 rounded-lg">
                        <ProduitSectionHeader
                          title="Ingrédients de la planche personnalisable"
                          helpKey="ingredientsPlanche"
                        />
                        <p className="text-xs text-gray-600 mb-3">
                          Sélectionnez les ingrédients que le client pourra choisir pour composer sa planche.
                          <span className="block mt-1 font-medium text-orange-700">
                            ⚠️ Ces ingrédients sont indépendants de la section "Ingrédients & allergènes" et ne déclenchent PAS la désactivation automatique en cas de rupture.
                          </span>
                        </p>

                        {/* Barre de recherche et bouton créer */}
                        <div className="mb-3 flex gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="text"
                              value={ingredientsSearch}
                              onChange={(e) => setIngredientsSearch(e.target.value)}
                              placeholder="Rechercher un ingrédient (jambon, fromage, tapas...)"
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setNewIngredientData({ 
                                nom: ingredientsSearch.trim() || '', 
                                description: '', 
                                allergenes: [] 
                              });
                              setNewIngredientAllergenes('');
                              setShowCreateIngredientModal(true);
                            }}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700"
                          >
                            <Plus className="h-4 w-4" />
                            Créer
                          </button>
                        </div>

                        {/* Message d'erreur */}
                        {ingredientsError && (
                          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start gap-2">
                              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                              <p className="text-xs text-red-700">{ingredientsError}</p>
                            </div>
                          </div>
                        )}

                        {/* Liste des ingrédients disponibles avec checkboxes */}
                        <div className="mb-3">
                          <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3">
                            {ingredientsLoading ? (
                              <div className="flex items-center justify-center py-6 text-sm text-gray-500">
                                <Loader2 className="h-5 w-5 animate-spin text-primary-500 mr-2" />
                                Chargement des ingrédients...
                              </div>
                            ) : filteredIngredients.length === 0 ? (
                              <div className="flex flex-col items-center justify-center gap-2 py-6 text-sm text-gray-500">
                                <span>Aucun ingrédient trouvé</span>
                                {ingredientsSearch.trim() && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setNewIngredientData({ 
                                        nom: ingredientsSearch.trim() || '', 
                                        description: '', 
                                        allergenes: [] 
                                      });
                                      setNewIngredientAllergenes('');
                                      setShowCreateIngredientModal(true);
                                    }}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                                  >
                                    <Plus className="h-3 w-3" />
                                    Créer "{ingredientsSearch.trim() || 'cet ingrédient'}"
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {filteredIngredients.map((ingredient) => {
                                  const isChecked = formData.ingredientIdsPlanche.includes(ingredient.id);
                                  return (
                                    <label
                                      key={ingredient.id}
                                      className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          const newChecked = e.target.checked;
                                          setFormData((prev) => {
                                            const current = prev.ingredientIdsPlanche || [];
                                            if (newChecked) {
                                              if (current.includes(ingredient.id)) return prev;
                                              return {
                                                ...prev,
                                                ingredientIdsPlanche: [...current, ingredient.id],
                                              };
                                            } else {
                                              return {
                                                ...prev,
                                                ingredientIdsPlanche: current.filter(
                                                  (id) => id !== ingredient.id
                                                ),
                                              };
                                            }
                                          });
                                      }}
                                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-gray-700 flex-1">
                                      {ingredient.nom}
                                      {ingredient.categorie && (
                                        <span className="ml-2 text-xs text-gray-400">
                                          ({ingredient.categorie})
                                        </span>
                                      )}
                                    </span>
                                    {ingredient.allergene && (
                                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600">
                                        Allergène
                                      </span>
                                    )}
                                  </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Récapitulatif */}
                        {formData.ingredientIdsPlanche.length > 0 && (
                          <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                            <p className="text-xs font-semibold text-green-800 mb-1">
                              Ingrédients sélectionnés pour la planche ({formData.ingredientIdsPlanche.length}) :
                            </p>
                            <p className="text-xs text-green-700">
                              {formData.ingredientIdsPlanche
                                .map(id => {
                                  const ing = ingredientsCatalog.find(i => i.id === id);
                                  return ing?.nom || id;
                                })
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Section "Disponible sur les plateaux suivants" (pour tous les produits SAUF les plateaux eux-mêmes) */}
                    {showSecForType('lienPlateaux') && !formData.isPlateauParPersonne && (
                      <div className="mt-4 p-4 bg-amber-50/50 border border-amber-200 rounded-lg">
                        <ProduitSectionHeader title="Disponible sur les planches" helpKey={SK.LIEN_PLATEAUX} />
                        <p className="text-xs text-gray-600 mb-3">
                          Cochez les plateaux sur lesquels ce produit (ingrédient) peut être utilisé.
                          <span className="block mt-1 font-medium text-blue-700">
                            💡 Exemple : La mortadelle peut être disponible sur "Planche charcuterie" et "Planche mixte".
                          </span>
                        </p>

                        {loadingPlateaux ? (
                          <div className="flex items-center justify-center py-6 text-sm text-gray-500">
                            <Loader2 className="h-5 w-5 animate-spin text-primary-500 mr-2" />
                            Chargement des plateaux...
                          </div>
                        ) : plateauxDisponibles.length === 0 ? (
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-sm text-yellow-800">
                              ℹ️ Aucun plateau par personne n'a encore été créé pour ce commerçant.
                            </p>
                            <p className="text-xs text-yellow-700 mt-1">
                              Créez d'abord un produit avec "C'est un plateau / une planche par personne" activé, puis revenez ici pour associer ce produit aux plateaux.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {plateauxDisponibles.map((plateau) => (
                              <label
                                key={plateau.id}
                                className="flex items-center gap-2 p-2 hover:bg-blue-100 rounded cursor-pointer border border-blue-200 bg-white"
                              >
                                <input
                                  type="checkbox"
                                  checked={plateauxIdsDisponibles.includes(plateau.id)}
                                  onChange={() => {
                                    const newIds = plateauxIdsDisponibles.includes(plateau.id)
                                      ? plateauxIdsDisponibles.filter(id => id !== plateau.id)
                                      : [...plateauxIdsDisponibles, plateau.id];
                                    setPlateauxIdsDisponibles(newIds);
                                  }}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 flex-1">
                                  {plateau.nom}
                                  {plateau.prixParPersonne && (
                                    <span className="ml-2 text-xs text-gray-400">
                                      ({plateau.prixParPersonne}€/personne)
                                    </span>
                                  )}
                                </span>
                              </label>
                            ))}
                          </div>
                        )}

                        {plateauxIdsDisponibles.length > 0 && (
                          <div className="mt-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                            <p className="text-xs font-semibold text-green-800 mb-1">
                              Plateaux sélectionnés ({plateauxIdsDisponibles.length}) :
                            </p>
                            <p className="text-xs text-green-700">
                              {plateauxIdsDisponibles
                                .map(id => {
                                  const plateau = plateauxDisponibles.find(p => p.id === id);
                                  return plateau?.nom || id;
                                })
                                .filter(Boolean)
                                .join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Vente au poids / à la coupe - MASQUÉ pour COMMERCE_NON_FOOD */}
                {showSecForType('venteAuPoids') && formData.productType !== 'COMMERCE_NON_FOOD' && (
                <div className="md:col-span-2 border-t pt-4 mt-2 rounded-lg border border-amber-100 bg-amber-50/30 p-4" data-produit-section="venteAuPoids">
                  <ProduitSectionHeader title="Vente au poids" helpKey={SK.VENTE_AU_POIDS} />
                  {!lockVenteAuPoidsToggle && (
                  <label className="flex items-center space-x-2 mb-4">
                    <input
                      type="checkbox"
                      checked={formData.venteAuPoids || false}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData(applyVenteAuPoidsToggle(formData, checked));
                      }}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="font-semibold text-gray-700">🥩 Vente au poids / à la coupe</span>
                  </label>
                  )}

                  {(lockVenteAuPoidsToggle || formData.venteAuPoids) && (
                    <div className="ml-6 space-y-4 bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Prix au poids (€/kg) *
                          </label>
                          <div className="relative">
                            <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                              type="number"
                              step="0.01"
                              value={formData.prixAuPoids || ''}
                              onChange={(e) => setFormData({...formData, prixAuPoids: e.target.value})}
                              onWheel={preventNumberScroll}
                              className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${formFieldErrorClass(PRODUIT_FORM_ISSUE_IDS.PRIX_AU_POIDS)}`}
                              placeholder="54.90"
                              required
                            />
                          </div>
                          {hasFormBlocker(PRODUIT_FORM_ISSUE_IDS.PRIX_AU_POIDS) ? (
                            <p className="text-xs text-red-600 mt-1">
                              Indiquez un prix au poids (€/kg ou unité choisie).
                            </p>
                          ) : (
                          <p className="text-xs text-gray-500 mt-1">
                            Le prix unitaire standard sera ignoré pour ce produit
                          </p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Unité de poids
                          </label>
                          <select
                            value={formData.unitePoids || 'kg'}
                            onChange={(e) => setFormData({...formData, unitePoids: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          >
                            <option value="kg">Par kilogramme (kg)</option>
                            <option value="100g">Par 100 grammes</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Poids portion standard (grammes)
                          </label>
                          <input
                            type="number"
                            step="5"
                            value={formData.poidsPortionStandard || ''}
                            onChange={(e) => setFormData({...formData, poidsPortionStandard: e.target.value})}
                            onWheel={preventNumberScroll}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="50"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Poids estimé pour une portion (ex: 50g = 1 pers.)
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre de tranches approximatif
                          </label>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            value={formData.nombreTranchesParPortion || ''}
                            onChange={(e) => setFormData({...formData, nombreTranchesParPortion: e.target.value})}
                            onWheel={preventNumberScroll}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="4"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Nombre de tranches approximatif pour cette portion (ex: 4 tranches)
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Types de découpe disponibles
                        </label>
                        <div className="space-y-2">
                          {['chiffonade', 'fine', 'normale', 'epaisse'].map((decoupe) => (
                            <label key={decoupe} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={(formData.decoupesDisponibles || []).includes(decoupe)}
                                onChange={(e) => {
                                  const order = ['chiffonade', 'fine', 'normale', 'epaisse'];
                                  const current = formData.decoupesDisponibles || [];
                                  const next = e.target.checked
                                    ? [...current, decoupe]
                                    : current.filter((d) => d !== decoupe);
                                  setFormData({
                                    ...formData,
                                    decoupesDisponibles: order.filter((d) => next.includes(d)),
                                  });
                                }}
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="capitalize text-sm text-gray-700">
                                {decoupe === 'epaisse' ? 'Épaisse' : decoupe}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
              )}

              {/* 4. Ingrédients & allergènes - MASQUÉ pour COMMERCE_NON_FOOD */}
              {showSecForType('ingredients') && formData.productType !== 'COMMERCE_NON_FOOD' && (
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <ProduitSectionHeader title="Ingrédients & allergènes" helpKey={SK.INGREDIENTS} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            value={ingredientsSearch}
                            onChange={(e) => setIngredientsSearch(e.target.value)}
                            placeholder="Rechercher un ingrédient (riz, saumon, salade...)"
                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                        {ingredientsLoading && (
                          <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          // Si une recherche est en cours, créer un ingrédient avec ce nom
                          if (ingredientsSearch.trim()) {
                            setNewIngredientData({ 
                              nom: ingredientsSearch.trim(), 
                              description: '', 
                              allergenes: [] 
                            });
                          } else {
                            setNewIngredientData({ nom: '', description: '', allergenes: [] });
                          }
                          setNewIngredientAllergenes('');
                          setShowCreateIngredientModal(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 whitespace-nowrap"
                        title="Créer un nouvel ingrédient"
                      >
                        <Plus className="h-4 w-4" />
                        Créer
                      </button>
                      {selectedIngredientIds.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedIngredientIds([])}
                          className="text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
                        >
                          Tout désélectionner
                        </button>
                      )}
                    </div>

                    {ingredientsError && (
                      <div className="flex items-start space-x-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
                        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
                        <p className="text-xs text-red-700">{ingredientsError}</p>
                      </div>
                    )}

                    <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200">
                      {ingredientsLoading ? (
                        <div className="flex items-center justify-center py-6 text-sm text-gray-500">
                          Chargement des ingrédients...
                        </div>
                      ) : filteredIngredients.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-2 py-6 text-sm text-gray-500">
                          <span>Aucun ingrédient trouvé</span>
                          <button
                            type="button"
                            onClick={() => {
                              setNewIngredientData({ 
                                nom: ingredientsSearch.trim() || '', 
                                description: '', 
                                allergenes: [] 
                              });
                              setNewIngredientAllergenes('');
                              setShowCreateIngredientModal(true);
                            }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                          >
                            <Plus className="h-3 w-3" />
                            Créer "{ingredientsSearch.trim() || 'cet ingrédient'}"
                          </button>
                        </div>
                      ) : (
                        <ul className="divide-y divide-gray-100">
                          {filteredIngredients.map((ingredient) => (
                            <li
                              key={ingredient.id}
                              className="flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition"
                            >
                              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedIngredientIds.includes(ingredient.id)}
                                  onChange={() => toggleIngredientSelection(ingredient.id)}
                                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span>
                                  {ingredient.nom}
                                  {ingredient.categorie && (
                                    <span className="ml-2 text-xs text-gray-400">
                                      ({ingredient.categorie})
                                    </span>
                                  )}
                                </span>
                              </label>
                              {ingredient.allergene && (
                                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-600">
                                  Allergène
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {selectedIngredientNames.length > 0 && (
                      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-green-800 mb-1">
                              Ingrédients sélectionnés ({selectedIngredientNames.length}) :
                            </p>
                            <p className="text-xs text-green-700">
                              {selectedIngredientNames.join(', ')}
                            </p>
                            <p className="text-xs text-green-600 mt-2">
                              ✓ Ces ingrédients seront automatiquement affichés sur la fiche produit client
                            </p>
                            <p className="text-xs text-green-600">
                              ✓ Ils seront pris en compte pour la désactivation automatique en cas de rupture
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Ingrédients affichés au client
                    </label>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 max-h-48 overflow-y-auto">
                      {selectedIngredientIds.length === 0 ? (
                        <p className="text-xs text-gray-500 text-center py-4">
                          Aucun ingrédient sélectionné. Les ingrédients sélectionnés apparaîtront ici.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {selectedIngredientIds.map((ingredientId) => {
                            const ingredient = ingredientsCatalog.find((ing) => ing.id === ingredientId);
                            if (!ingredient) return null;
                            const isActive = ingredient.actif !== false;
                            return (
                              <div
                                key={ingredientId}
                                className="flex items-center justify-between px-2 py-1.5 rounded bg-white border border-gray-200"
                              >
                                <div className="flex items-center gap-2 flex-1">
                                  {isActive ? (
                                    <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                                  ) : (
                                    <X className="h-4 w-4 text-red-600 flex-shrink-0" />
                                  )}
                                  <span className={`text-sm ${isActive ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                                    {ingredient.nom}
                                  </span>
                                  {ingredient.allergene && (
                                    <span className="ml-2 rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-600">
                                      Allergène
                                    </span>
                                  )}
                                </div>
                                <span className={`text-xs font-medium ${isActive ? 'text-green-600' : 'text-red-600'}`}>
                                  {isActive ? 'Actif' : 'Inactif'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Récapitulatif des ingrédients qui seront affichés sur la fiche produit client.
                      <span className="block mt-1">
                        <span className="inline-flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span>Actif</span>
                        </span>
                        {' • '}
                        <span className="inline-flex items-center gap-1">
                          <X className="h-3 w-3 text-red-600" />
                          <span>Inactif</span>
                        </span>
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              )}

              {/* 5. Catégorie */}
              {showSecForType('categorie') && (
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <ProduitSectionHeader title="Catégorie" helpKey={SK.CATEGORIE} />
                <div className="grid grid-cols-2 gap-4">
                  <div className="relative">
                    {!showNewCategoryInput ? (
                      <div className="flex gap-2">
                        <select
                          value={formData.categorie}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '__new__') {
                              setShowNewCategoryInput(true);
                              setFormData({ ...formData, categorie: '', sousCategorie: '' });
                            } else {
                              setFormData({ ...formData, categorie: value, sousCategorie: '' });
                              setShowNewSubCategoryInput(false);
                            }
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          disabled={loadingCategories}
                        >
                          <option value="">-- Sélectionner une catégorie --</option>
                          {existingCategories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                          <option value="__new__">+ Ajouter une nouvelle catégorie</option>
                        </select>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.categorie}
                          onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                          onBlur={() => {
                            if (!formData.categorie.trim()) {
                              setShowNewCategoryInput(false);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setShowNewCategoryInput(false);
                              setFormData({ ...formData, categorie: '' });
                            }
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Nouvelle catégorie (ex: FROMAGES)"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            const nom = formData.categorie.trim();
                            if (nom && !existingCategories.includes(nom)) {
                              await ensureCatalogueRubrique(1, nom);
                              setExistingCategories([...existingCategories, nom].sort((a, b) =>
                                a.localeCompare(b, 'fr', { sensitivity: 'base' })
                              ));
                            }
                            setShowNewCategoryInput(false);
                          }}
                          className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewCategoryInput(false);
                            setFormData({ ...formData, categorie: '' });
                          }}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    {!formData.categorie ? (
                      <p className="text-sm text-gray-400 px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
                        Choisissez d&apos;abord une catégorie
                      </p>
                    ) : !showNewSubCategoryInput ? (
                      <select
                        value={formData.sousCategorie}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '__new__') {
                            setShowNewSubCategoryInput(true);
                            setFormData({ ...formData, sousCategorie: '' });
                          } else {
                            setFormData({ ...formData, sousCategorie: value });
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        disabled={loadingCategories}
                      >
                        <option value="">-- Sous-catégorie (optionnel) --</option>
                        {existingSubCategories.map((sub) => (
                          <option key={sub} value={sub}>
                            {sub}
                          </option>
                        ))}
                        <option value="__new__">+ Ajouter une sous-catégorie</option>
                      </select>
                    ) : (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={formData.sousCategorie}
                          onChange={(e) => setFormData({ ...formData, sousCategorie: e.target.value })}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Nouvelle sous-catégorie (ex: Vapeurs)"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            const nom = formData.sousCategorie.trim();
                            if (nom && selectedCategoryNode?.id) {
                              await ensureCatalogueRubrique(2, nom, selectedCategoryNode.id);
                            }
                            setShowNewSubCategoryInput(false);
                          }}
                          className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                        >
                          ✓
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewSubCategoryInput(false);
                            setFormData({ ...formData, sousCategorie: '' });
                          }}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {formData.categorie && !existingCategories.includes(formData.categorie) && !showNewCategoryInput && (
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Cette catégorie sera créée automatiquement
                  </p>
                )}
              </div>
              )}

              {/* SERVICE catalogue — durée / disponibilité (≠ Prestation RDV) */}
              {isService && (
                <>
                  {produit?.id && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/80 p-5 space-y-3">
                      <h4 className="text-sm font-semibold text-emerald-950">
                        Réservation en ligne
                      </h4>
                      <p className="text-xs text-emerald-900/80">
                        Cette fiche catalogue n’est pas encore branchée sur le moteur de
                        réservation. Activez l’option pour créer une prestation réservable
                        (créneaux / RDV) à partir de ces informations — la fiche catalogue
                        actuelle sera désactivée pour éviter un doublon.
                      </p>
                      <button
                        type="button"
                        disabled={convertingToReservable}
                        onClick={handleEnableReservationFromService}
                        className="inline-flex items-center rounded-lg bg-emerald-700 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-60"
                      >
                        {convertingToReservable
                          ? 'Activation…'
                          : 'Activer la réservation en ligne'}
                      </button>
                    </div>
                  )}
                  <div
                    data-produit-section="duree"
                    className="rounded-xl border border-gray-200 bg-white p-5 space-y-3"
                  >
                    <h4 className="text-sm font-semibold text-gray-900">Durée de la prestation</h4>
                    <p className="text-xs text-gray-500">
                      Durée proposée au client (ex. 15, 30, 45, 60 minutes). Ce n’est pas un temps de préparation produit.
                    </p>
                    <div className="flex items-center gap-2 max-w-xs">
                      <input
                        type="number"
                        min={0}
                        step={5}
                        value={formData.dureeMinutes ?? ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dureeMinutes:
                              e.target.value === '' ? null : parseInt(e.target.value, 10),
                          })
                        }
                        onWheel={preventNumberScroll}
                        {...NUMBER_INPUT_PROPS}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        placeholder="15"
                      />
                      <span className="text-sm text-gray-600">minutes</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
                    <h4 className="text-sm font-semibold text-gray-900">Disponibilité</h4>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.actif !== false}
                        onChange={(e) =>
                          setFormData({ ...formData, actif: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm text-gray-800">Actif dans le catalogue</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.indisponible)}
                        onChange={(e) =>
                          setFormData({ ...formData, indisponible: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-gray-300 text-primary-600"
                      />
                      <span className="text-sm text-gray-800">
                        Indisponible (reste visible, non commandable)
                      </span>
                    </label>
                  </div>

                  <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-4">
                    <button
                      type="button"
                      onClick={() => setServiceAdvancedOpen((v) => !v)}
                      className="text-sm font-medium text-gray-700 hover:text-primary-700"
                    >
                      {serviceAdvancedOpen ? '▾' : '▸'} Options avancées (participants, équipement…)
                    </button>
                    {serviceAdvancedOpen && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Participants min.
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={formData.minParticipants ?? ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                minParticipants:
                                  e.target.value === ''
                                    ? null
                                    : parseInt(e.target.value, 10),
                              })
                            }
                            onWheel={preventNumberScroll}
                            {...NUMBER_INPUT_PROPS}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Participants max.
                          </label>
                          <input
                            type="number"
                            min={0}
                            value={formData.maxParticipants ?? ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                maxParticipants:
                                  e.target.value === ''
                                    ? null
                                    : parseInt(e.target.value, 10),
                              })
                            }
                            onWheel={preventNumberScroll}
                            {...NUMBER_INPUT_PROPS}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                          />
                        </div>
                        <label className="flex items-center gap-2 md:col-span-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(formData.equipementFourni)}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                equipementFourni: e.target.checked,
                              })
                            }
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-800">Équipement fourni</span>
                        </label>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Préparation requise (consignes client)
                          </label>
                          <textarea
                            value={formData.preparationRequis || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                preparationRequis: e.target.value,
                              })
                            }
                            rows={2}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                            placeholder="Optionnel — consignes avant le rendez-vous…"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* 5. Champs spécifiques au type de commerce */}
              {showSecForType('champsMetier') && commercantType && (
                <>
                  {commercantType === 'CENTRE_EQUESTRE' && (
                    <ProduitFieldsCentreEquestre formData={formData} setFormData={setFormData} />
                  )}
                </>
              )}

              {/* Temps de préparation (produits physiques) */}
              {showSecForType('champsMetier') && !isService && (
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <ProduitSectionHeader title="Temps de préparation" />
                  <div className="space-y-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        className="mt-1"
                        checked={!formData.tempsPreparationPersonnalise}
                        onChange={() =>
                          setFormData({ ...formData, tempsPreparationPersonnalise: false })
                        }
                      />
                      <span className="text-sm text-gray-700">
                        Utiliser le délai par défaut de la boutique
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Actuellement : {commercantDefaultPrep} min
                        </span>
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        className="mt-1"
                        checked={Boolean(formData.tempsPreparationPersonnalise)}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            tempsPreparationPersonnalise: true,
                            // Champ vide pour saisie directe (évite "0" → "035")
                            tempsPreparation: '',
                          })
                        }
                      />
                      <span className="text-sm text-gray-700 w-full">
                        Définir un délai spécifique
                        {formData.tempsPreparationPersonnalise && (
                          <input
                            type="number"
                            min="0"
                            max="480"
                            value={
                              formData.tempsPreparation === '' ||
                              formData.tempsPreparation === null ||
                              formData.tempsPreparation === undefined
                                ? ''
                                : formData.tempsPreparation
                            }
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') {
                                setFormData({ ...formData, tempsPreparation: '' });
                                return;
                              }
                              const n = parseInt(raw, 10);
                              if (!Number.isFinite(n)) return;
                              setFormData({ ...formData, tempsPreparation: n });
                            }}
                            onBlur={() => {
                              if (
                                formData.tempsPreparation === '' ||
                                formData.tempsPreparation === null ||
                                formData.tempsPreparation === undefined
                              ) {
                                return;
                              }
                              const n = parseInt(formData.tempsPreparation, 10);
                              if (Number.isFinite(n) && n >= 0) {
                                setFormData({ ...formData, tempsPreparation: n });
                              }
                            }}
                            className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="Ex: 30"
                          />
                        )}
                      </span>
                    </label>
                  </div>
                </div>
              )}

              {/* 6. Stock */}
              {showSecForType('stock') && (
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <ProduitSectionHeader title="Gestion du stock" helpKey={SK.STOCK} />
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={!formData.stockLimite}
                      onChange={() => setFormData({ ...formData, stockLimite: false, stock: -1 })}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700">Stock illimité (recommandé)</span>
                  </label>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.stockLimite}
                      onChange={() => setFormData({ ...formData, stockLimite: true, stock: 10 })}
                      className="w-4 h-4 text-primary-600"
                    />
                    <span className="text-sm text-gray-700">Stock limité</span>
                  </label>
                  {formData.stockLimite && (
                    <div className="ml-7 flex items-center space-x-3">
                      <input
                        type="number"
                        value={formData.stock === -1 ? '' : formData.stock}
                        onChange={(e) => {
                          const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0
                          setFormData({ ...formData, stock: Math.max(0, val) })
                        }}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg"
                        min="0"
                        placeholder="0"
                      />
                      <span className="text-sm text-gray-600">en stock</span>
                      {formData.stock < 5 && formData.stock >= 0 && (
                        <span className="text-xs text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Stock faible !
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )}

              {/* A2.0 — Expédition (produits physiques, moduleExpedition) */}
              {moduleExpeditionEnabled && !isService && (
                <div
                  data-produit-section="expedition"
                  className="rounded-xl border border-gray-200 bg-white p-5 space-y-4"
                >
                  <ProduitSectionHeader title="Expédition" />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Profil colis
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      value={formData.shippingProfileId || ''}
                      disabled={loadingShippingProfiles}
                      onChange={(e) => {
                        const nextId = e.target.value || null;
                        setFormData({
                          ...formData,
                          shippingProfileId: nextId,
                          shippingWeightGrams: nextId ? formData.shippingWeightGrams : '',
                        });
                      }}
                    >
                      {buildExpeditionProfileSelectOptions(shippingProfiles).map((opt) => (
                        <option key={opt.value || 'none'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    {shippingProfilesLoadError ? (
                      <p className="mt-1 text-xs text-red-600">{shippingProfilesLoadError}</p>
                    ) : null}
                    {formData.shippingProfileId &&
                      shippingProfiles.some(
                        (p) => p.id === formData.shippingProfileId && p.isActive === false
                      ) && (
                        <p className="mt-1 text-xs text-amber-700">
                          Ce profil est inactif — le produit restera non éligible à l&apos;expédition
                          tant que le profil n&apos;est pas réactivé.
                        </p>
                      )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Poids spécifique pour l&apos;expédition (g)
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Optionnel"
                      disabled={!formData.shippingProfileId}
                      value={formData.shippingWeightGrams}
                      onChange={(e) =>
                        setFormData({ ...formData, shippingWeightGrams: e.target.value })
                      }
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Laisser vide pour utiliser le poids défini dans le profil colis.
                    </p>
                  </div>
                </div>
              )}

              {/* 7. Mode Composition Personnalisée - MASQUÉ pour COMMERCE_NON_FOOD et pour les planches par personne */}
              {showSecForType('compositionPieces') && formData.productType !== 'COMMERCE_NON_FOOD' && !formData.isPlateauParPersonne && (
              <div data-produit-section="compositionPieces" className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
              <ProduitSectionHeader title="Plateau à composer (par pièces)" helpKey={SK.COMPOSITION_PIECES} />
              <div className="border border-amber-100 rounded-lg p-4 bg-white/80">
                {archetypeId === ARCHETYPE_IDS.PLATEAU_PIECES && (
                  <p className="text-sm font-medium text-blue-900 bg-white border border-blue-300 rounded-lg px-3 py-2 mb-4">
                    💰 Renseignez le <strong>prix de chaque taille</strong> dans la section « Tailles
                    disponibles » ci-dessous — c&apos;est le tarif affiché au client. Le prix catalogue
                    classique reste optionnel dans « Enrichir la fiche ».
                  </p>
                )}
                {!lockCompositionToggle && (
                <label className="flex items-center space-x-3 cursor-pointer mb-4">
                  <input
                    type="checkbox"
                    checked={formData.modeComposition}
                    onChange={(e) => setFormData({ ...formData, modeComposition: e.target.checked })}
                    className="w-5 h-5 text-primary-600"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-900">
                      Activer le mode composition
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      Active ce mode pour les produits à composer (ex: plateau 12 pièces)
                    </p>
                  </div>
                </label>
                )}

                {(lockCompositionToggle || formData.modeComposition) && (
                  <div className="space-y-4 mt-4 pt-4 border-t border-blue-300">
                    <div className="grid grid-cols-3 gap-4">
                      {/* Incrément */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Incrément (pièces)
                        </label>
                        <select
                          value={formData.incrementPieces}
                          onChange={(e) => setFormData({ ...formData, incrementPieces: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="1">1 pièce</option>
                          <option value="2">2 pièces</option>
                          <option value="4">4 pièces</option>
                          <option value="6">6 pièces</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Quantité ajoutée à chaque clic
                        </p>
                      </div>

                      {/* Limite totale — masquée en mode guidé (champ trompeur) */}
                      {!guidedMode && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Limite totale (optionnel)
                        </label>
                        <input
                          type="number"
                          value={formData.limiteTotal || ''}
                          onChange={(e) => setFormData({ ...formData, limiteTotal: e.target.value ? parseInt(e.target.value) : null })}
                          className={`w-full px-3 py-2 border rounded-lg text-sm ${formFieldErrorClass(PRODUIT_FORM_ISSUE_IDS.LIMITE_TOTAL)}`}
                          placeholder="Ex: 12"
                          min="1"
                        />
                        {hasFormBlocker(PRODUIT_FORM_ISSUE_IDS.LIMITE_TOTAL) ? (
                          <p className="text-xs text-red-600 mt-1">
                            Indiquez une limite de pièces, ou ajoutez des tailles de plateau (validation stricte).
                          </p>
                        ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          Max de pièces pour le plateau
                        </p>
                        )}
                      </div>
                      )}

                      {/* Mode de validation */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          Validation
                        </label>
                        <select
                          value={formData.validationMode}
                          onChange={(e) => setFormData({ ...formData, validationMode: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="FLEXIBLE">Flexible</option>
                          <option value="MINIMUM">Minimum requis</option>
                          <option value="EXACT">Exact uniquement</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.validationMode === 'EXACT' && 'Doit être exactement la limite'}
                          {formData.validationMode === 'MINIMUM' && 'Au moins la limite'}
                          {formData.validationMode === 'FLEXIBLE' && 'Aucune contrainte'}
                        </p>
                      </div>
                    </div>

                    {/* Exemple visuel */}
                    <div className="bg-white border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">📱 Aperçu client :</p>
                      <div className="text-xs text-gray-600 space-y-1">
                        {formData.limiteTotal && (
                          <p>• Plateau {formData.limiteTotal} pièces max</p>
                        )}
                        <p>• Compteurs par +{formData.incrementPieces}</p>
                        {formData.validationMode === 'EXACT' && formData.limiteTotal && (
                          <p className="text-orange-600">• Le client DOIT composer exactement {formData.limiteTotal} pièces</p>
                        )}
                        {formData.validationMode === 'MINIMUM' && formData.limiteTotal && (
                          <p className="text-blue-600">• Le client DOIT composer au moins {formData.limiteTotal} pièces</p>
                        )}
                        {formData.validationMode === 'FLEXIBLE' && (
                          <p className="text-green-600">• Le client compose librement</p>
                        )}
                      </div>
                    </div>

                    {/* Gestion des tailles de plateaux */}
                    <div className="mt-4 pt-4 border-t border-amber-200">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <ProduitSectionHeader
                          title="Tailles disponibles"
                          helpKey="taillesPlateau"
                          optional
                          className="mb-0 flex-1"
                        />
                        <button
                          type="button"
                          onClick={addTaillePlateau}
                          className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                        >
                          <Plus className="h-4 w-4" />
                          <span>Ajouter une taille</span>
                        </button>
                      </div>
                      
                      {loadingTailles ? (
                        <div className="text-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                          <p className="text-sm text-gray-600 mt-2">Chargement des tailles...</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {taillesPlateaux.length === 0 ? (
                            <div className="text-center py-6 bg-white border border-blue-200 rounded-lg">
                              <p className="text-sm text-gray-500 mb-2">Aucune taille définie</p>
                              <p className="text-xs text-gray-400">Ajoutez les tailles que le client peut choisir (ex: 8, 14, 18, 24 pièces)</p>
                            </div>
                          ) : (
                            taillesPlateaux.map((taille, index) => (
                              <div key={taille.id || index} className="bg-white border border-blue-200 rounded-lg p-3">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1 grid grid-cols-2 gap-3">
                                    <input
                                      type="text"
                                      value={taille.nom}
                                      onChange={(e) => updateTaillePlateau(index, 'nom', e.target.value)}
                                      placeholder="Nom (ex: 8 pièces, Plateau découverte)"
                                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                    <input
                                      type="number"
                                      value={taille.nombrePieces ?? ''}
                                      onChange={(e) => {
                                        const raw = e.target.value
                                        // Autoriser le champ vide le temps de la saisie
                                        if (raw === '') {
                                          updateTaillePlateau(index, 'nombrePieces', '')
                                        } else {
                                          const parsed = parseInt(raw, 10)
                                          updateTaillePlateau(index, 'nombrePieces', Number.isNaN(parsed) ? '' : parsed)
                                        }
                                      }}
                                      placeholder="Nombre de pièces"
                                      min="1"
                                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeTaillePlateau(index)}
                                    className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                                
                                <div className="grid grid-cols-1 gap-3">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">
                                      {archetypeId === ARCHETYPE_IDS.PLATEAU_PIECES
                                        ? 'Prix de cette taille (€) *'
                                        : 'Prix fixe (optionnel)'}
                                    </label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={taille.prix || ''}
                                      onChange={(e) => updateTaillePlateau(index, 'prix', e.target.value ? parseFloat(e.target.value) : null)}
                                      placeholder="Ex: 24.90"
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    />
                                  </div>
                                  
                                  {/* Limites par catégorie */}
                                  {elementsComposables.length > 0 && (() => {
                                    // Récupérer toutes les catégories uniques
                                    const categories = [...new Set(elementsComposables.map(el => el.categorie).filter(Boolean))];
                                    const limites = taille.limitesParCategorie || {};
                                    
                                    return (
                                      <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <label className="block text-xs font-medium text-gray-700 mb-2">
                                          Limites par catégorie (optionnel)
                                        </label>
                                        <p className="text-xs text-gray-500 mb-2">
                                          Définir le nombre maximum de pièces par catégorie pour cette taille
                                        </p>
                                        <div className="space-y-2">
                                          {categories.map((categorie) => (
                                            <div key={categorie} className="flex items-center gap-2">
                                              <label className="text-xs text-gray-600 w-24 flex-shrink-0">
                                                {categorie}:
                                              </label>
                                              <input
                                                type="number"
                                                min="0"
                                                value={limites[categorie] || ''}
                                                onChange={(e) => {
                                                  const newLimites = { ...limites };
                                                  const value = e.target.value ? parseInt(e.target.value) : null;
                                                  if (value !== null && value > 0) {
                                                    newLimites[categorie] = value;
                                                  } else {
                                                    delete newLimites[categorie];
                                                  }
                                                  updateTaillePlateau(index, 'limitesParCategorie', Object.keys(newLimites).length > 0 ? newLimites : null);
                                                }}
                                                placeholder="Max (vide = illimité)"
                                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                  💡 Si aucun prix n'est défini, le prix sera calculé selon les éléments choisis par le client
                                </p>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </div>
              )}

              {/* 6. Options & Variantes - SYSTÈME SIMPLE */}
              {showSecForType('options') && (
              <div className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <ProduitSectionHeader title="Options & variantes" helpKey={SK.OPTIONS} optional className="mb-0 flex-1" />
                  <button
                    onClick={addOption}
                    className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Ajouter une option</span>
                  </button>
                </div>

                {options.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-sm text-gray-500">Aucune option. Ajoutez des variantes de taille, sauce, etc.</p>
                    <button
                      onClick={addOption}
                      className="mt-3 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
                    >
                      ➕ Créer ma première option
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {options.map((option, optIndex) => (
                      <div key={option.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                        {/* Ligne 1 : nom + poubelle bien alignés */}
                        <div className="flex items-center justify-between mb-3">
                          <input
                            type="text"
                            value={option.nom}
                            onChange={(e) => updateOption(optIndex, 'nom', e.target.value)}
                            placeholder="Nom de l'option (ex: Taille, Durée, Sauce...)"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white"
                          />
                          <button
                            onClick={() => removeOption(optIndex)}
                            className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Ligne 2 : type / max / obligatoire, bien alignés */}
                        <div className="flex flex-wrap items-center gap-4 mb-3 pl-1">
                          {/* Type de sélection : choix unique / multiples */}
                          <div className="flex items-center space-x-3">
                            <span className="text-xs text-gray-500 whitespace-nowrap">Type :</span>
                            <button
                              type="button"
                              className={`px-2 py-1 rounded-lg text-xs border ${
                                option.type === 'CHOIX_UNIQUE'
                                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                                  : 'border-gray-300 text-gray-600'
                              }`}
                              onClick={() => updateOption(optIndex, 'type', 'CHOIX_UNIQUE')}
                            >
                              Choix unique
                            </button>
                            <button
                              type="button"
                              className={`px-2 py-1 rounded-lg text-xs border ${
                                option.type === 'CHOIX_MULTIPLE'
                                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                                  : 'border-gray-300 text-gray-600'
                              }`}
                              onClick={() => updateOption(optIndex, 'type', 'CHOIX_MULTIPLE')}
                            >
                              Choix multiples
                            </button>
                          </div>

                          {/* Min / Max / Inclus pour les choix multiples */}
                          {option.type === 'CHOIX_MULTIPLE' && (
                            <>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500 whitespace-nowrap">Min :</span>
                                <input
                                  {...NUMBER_INPUT_PROPS}
                                  type="number"
                                  min={0}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-xs"
                                  value={option.choixMinimum ?? 0}
                                  onChange={(e) =>
                                    updateOption(
                                      optIndex,
                                      'choixMinimum',
                                      e.target.value ? parseInt(e.target.value, 10) || 0 : 0
                                    )
                                  }
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500 whitespace-nowrap">Max :</span>
                                <label className="flex items-center gap-1.5 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(option.maxIllimite)}
                                    onChange={(e) => updateOption(optIndex, 'maxIllimite', e.target.checked)}
                                    className="w-3.5 h-3.5 text-primary-600"
                                  />
                                  <span className="text-xs text-gray-600">Illimité</span>
                                </label>
                                {!option.maxIllimite && (
                                  <input
                                    {...NUMBER_INPUT_PROPS}
                                    type="number"
                                    min={1}
                                    className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-xs"
                                    value={option.choixMaximum ?? ''}
                                    onChange={(e) =>
                                      updateOption(
                                        optIndex,
                                        'choixMaximum',
                                        e.target.value ? parseInt(e.target.value, 10) || 1 : null
                                      )
                                    }
                                  />
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-500 whitespace-nowrap">Inclus :</span>
                                <input
                                  {...NUMBER_INPUT_PROPS}
                                  type="number"
                                  min={0}
                                  className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-xs"
                                  value={option.nbInclus ?? 0}
                                  onChange={(e) =>
                                    updateOption(
                                      optIndex,
                                      'nbInclus',
                                      e.target.value ? parseInt(e.target.value, 10) || 0 : 0
                                    )
                                  }
                                />
                                <span className="text-xs text-gray-400">gratuit(s)</span>
                              </div>
                            </>
                          )}

                          {/* Option obligatoire */}
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={option.obligatoire}
                              onChange={(e) => updateOption(optIndex, 'obligatoire', e.target.checked)}
                              className="w-4 h-4 text-primary-600"
                            />
                            <span className="text-sm text-gray-700">Obligatoire</span>
                          </label>
                        </div>

                        {/* Choix de l'option */}
                        <div className="ml-4 space-y-2">
                          {option.choix.map((choix, choixIndex) => (
                            <div key={choix.id} className="space-y-1">
                              <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={choix.nom}
                                onChange={(e) => updateChoix(optIndex, choixIndex, 'nom', e.target.value)}
                                placeholder="Nom du choix (ex: 1h, 2h, Grande...)"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                              />
                              <div className="flex items-center space-x-1">
                                <span className="text-sm text-gray-500">+</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={choix.prix ?? ''}
                                  onChange={(e) => {
                                    updateChoix(optIndex, choixIndex, 'prix', e.target.value);
                                  }}
                                  className="w-20 px-2 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                                  placeholder="0.00"
                                />
                                <span className="text-sm text-gray-500">€</span>
                              </div>
                              <button
                                onClick={() => removeChoix(optIndex, choixIndex)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <X className="h-4 w-4" />
                              </button>
                              </div>
                              <div className="flex items-center space-x-2 pl-1">
                                <label className="text-xs text-gray-500 whitespace-nowrap">
                                  Produit lié pour le stock
                                </label>
                                <select
                                  value={choix.linkedProduitId || ''}
                                  onChange={(e) =>
                                    updateChoix(
                                      optIndex,
                                      choixIndex,
                                      'linkedProduitId',
                                      e.target.value || null
                                    )
                                  }
                                  className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg bg-white text-xs"
                                  disabled={loadingProduitsCatalogueStock}
                                >
                                  <option value="">— Aucun (historique) —</option>
                                  {produitsCatalogueStock.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.nom}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={() => addChoixToOption(optIndex)}
                            className="text-sm text-primary-600 hover:text-primary-700 flex items-center space-x-1"
                          >
                            <Plus className="h-4 w-4" />
                            <span>Ajouter un choix</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* Badges alimentaires dérivés : plus de checkboxes.
                  Source de vérité utilisateur = Caractéristiques (Bio, Vegan, Local…).
                  Allergènes légaux restent dans « Ingrédients & allergènes ». */}
              {isCreationMode && showSecForType('badges') && formData.typeProduit === 'PHYSIQUE' && (
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <ProduitSectionHeader title="Caractéristiques" optional />
                  <p className="text-sm text-gray-600">
                    Après la création du produit, ajoutez Bio, Local, Vegan et les autres mentions
                    dans la section Caractéristiques.
                  </p>
                </div>
              )}

              {/* Caractéristiques — mémoire métier par commerçant (M2), disponible uniquement en édition */}
              {produit?.id && !isService && (
                <div className="rounded-xl border border-gray-200 bg-white p-5">
                  <ProduitSectionHeader title="Caractéristiques" optional />
                  <p className="text-xs text-gray-500 mb-3">
                    Ajoutez ce qui distingue ce produit (jusqu&apos;à 3 mises en avant avec l&apos;étoile).
                  </p>
                  <CaracteristiquesChips
                    commercantId={commercantId}
                    produitId={produit.id}
                    initialItems={produit.caracteristiques || []}
                    legacyOrphans={legacyOrphans}
                    onLegacyAdopted={(field) =>
                      setLegacyOrphans((prev) => prev.filter((o) => o.field !== field))
                    }
                  />
                </div>
              )}

              {isCreationMode && !isService && (
                <ProduitCasParticuliers
                  formData={formData}
                  showAllSections={showAllSections}
                  onShortcutToggle={handleCreationShortcutToggle}
                  onShowAllSections={handleShowAllSections}
                  onShowSimplified={handleShowSimplified}
                />
              )}

              {/* 8. Éléments Composables (si mode composition activé) - MASQUÉ pour COMMERCE_NON_FOOD */}
              {showSecForType('elementsComposables') && formData.modeComposition && formData.productType !== 'COMMERCE_NON_FOOD' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <ProduitSectionHeader
                      title="Pièces composables"
                      helpKey={SK.ELEMENTS_COMPOSABLES}
                      optional
                      className="mb-0 flex-1"
                    />
                    <button
                      onClick={addElementComposable}
                      className="flex items-center space-x-1 text-sm text-blue-600 hover:text-blue-700"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Ajouter</span>
                    </button>
                  </div>
                  
                  {loadingElements ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="text-sm text-gray-600 mt-2">Chargement des éléments...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {elementsComposables.length === 0 ? (
                        <div className="text-center py-6 bg-white border border-blue-200 rounded-lg">
                          <p className="text-sm text-gray-500 mb-2">Aucun élément composable défini</p>
                          <p className="text-xs text-gray-400">Ajoutez les éléments que le client peut choisir</p>
                        </div>
                      ) : (
                        elementsComposables.map((element, index) => (
                          <div key={element.id} className="bg-white border border-blue-200 rounded-lg p-3">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 grid grid-cols-2 gap-3">
                                <input
                                  type="text"
                                  value={element.nom}
                                  onChange={(e) => updateElementComposable(index, 'nom', e.target.value)}
                                  placeholder="Nom de l'élément (ex: Sushi Saumon)"
                                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <input
                                  type="text"
                                  value={element.categorie || ''}
                                  onChange={(e) => updateElementComposable(index, 'categorie', e.target.value)}
                                  placeholder="Catégorie (ex: SUSHI, MAKI) *"
                                  required
                                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                              <button
                                onClick={() => removeElementComposable(index)}
                                className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-3 mb-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Prix (€)
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={element.prix}
                                  onChange={(e) => updateElementComposable(index, 'prix', parseFloat(e.target.value) || 0)}
                                  placeholder="Ex: 1.50"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Limite max (optionnelle)
                                </label>
                                <input
                                  type="number"
                                  value={element.limiteMax || ''}
                                  onChange={(e) => updateElementComposable(index, 'limiteMax', e.target.value ? parseInt(e.target.value) : null)}
                                  placeholder="Vide = illimité"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Ordre d'affichage
                                </label>
                                <input
                                  type="number"
                                  value={element.ordre}
                                  onChange={(e) => updateElementComposable(index, 'ordre', parseInt(e.target.value) || 0)}
                                  placeholder="0, 1, 2..."
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                              </div>
                            </div>
                            
                            <textarea
                              value={element.description || ''}
                              onChange={(e) => updateElementComposable(index, 'description', e.target.value)}
                              placeholder="Description (optionnelle)"
                              rows="2"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 9. Templates de Produits - MASQUÉ si aucun template n'existe */}
              {showSecForType('templates') && templates.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
                  <ProduitSectionHeader title="Modèles (templates)" helpKey={SK.TEMPLATES} optional />
                  
                  {loadingTemplates ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                      <p className="text-sm text-gray-600 mt-2">Chargement des templates...</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {templates.map((template) => (
                          <div
                            key={template.id}
                            className="bg-white border border-purple-200 rounded-lg p-3 hover:shadow-md transition"
                          >
                            <h4 className="text-sm font-medium text-gray-900 mb-1">
                              {template.nom}
                            </h4>
                            <p className="text-xs text-gray-600 mb-2">
                              {template.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-purple-600">
                                {template.categorie}
                              </span>
                              <button
                                onClick={() => applyTemplate(template)}
                                className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition"
                              >
                                Appliquer
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 10. Contraintes Avancées - MASQUÉES par défaut (section non utilisée) */}
              {/* Section masquée car peu utilisée et trop complexe pour 99% des commerçants */}
              {/* Si besoin, décommenter et ajouter un toggle "Options avancées" */}
              {false && (
                <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-semibold text-gray-700">
                      🔟 Contraintes Avancées <span className="text-xs font-normal text-gray-500">(Règles de validation)</span>
                    </label>
                    <button
                      onClick={addContrainte}
                      className="flex items-center space-x-1 text-sm text-orange-600 hover:text-orange-700"
                    >
                      <Plus className="h-4 w-4" />
                      <span>Ajouter</span>
                    </button>
                  </div>

                  {contraintes.length === 0 ? (
                    <div className="text-center py-6 bg-white border border-orange-200 rounded-lg">
                      <p className="text-sm text-gray-500 mb-2">Aucune contrainte définie</p>
                      <p className="text-xs text-gray-400">Ajoutez des règles de validation personnalisées</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contraintes.map((contrainte, index) => (
                        <div key={contrainte.id} className="bg-white border border-orange-200 rounded-lg p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1 grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={contrainte.nom}
                                onChange={(e) => updateContrainte(index, 'nom', e.target.value)}
                                placeholder="Nom de la contrainte"
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              />
                              <select
                                value={contrainte.type}
                                onChange={(e) => updateContrainte(index, 'type', e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                              >
                                <option value="QUANTITY">Quantité</option>
                                <option value="EXCLUSION">Exclusion</option>
                                <option value="PRICE">Prix</option>
                                <option value="AVAILABILITY">Disponibilité</option>
                              </select>
                            </div>
                            <button
                              onClick={() => removeContrainte(index)}
                              className="ml-2 p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          
                          <textarea
                            value={contrainte.description}
                            onChange={(e) => updateContrainte(index, 'description', e.target.value)}
                            placeholder="Description de la contrainte"
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2"
                          />
                          
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={contrainte.valeur}
                              onChange={(e) => updateContrainte(index, 'valeur', e.target.value)}
                              placeholder="Valeur (ex: 2, 10€, etc.)"
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <input
                              type="text"
                              value={contrainte.message}
                              onChange={(e) => updateContrainte(index, 'message', e.target.value)}
                              placeholder="Message d'erreur"
                              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Validation finale */}
              {formData.nom?.trim() && formCanSubmit && (
                <div className="flex items-start space-x-2 text-green-700 bg-green-50 p-4 rounded-lg border border-green-200">
                  <Check className="h-5 w-5 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Votre produit peut être enregistré</p>
                    <p className="text-sm mt-1">
                      {formData.images.length > 0
                        ? 'Cliquez sur « Enregistrer » pour le publier dans votre menu.'
                        : 'Cliquez sur « Enregistrer ». Ajoutez des photos pour mettre votre produit en valeur sur le menu.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {!formCanSubmit && <ProduitSaveBlockers issues={formBlockingIssues} />}

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition"
              disabled={saving}
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving || !formCanSubmit}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Enregistrement...</span>
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  <span>💾 Enregistrer</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Modal de création d'ingrédient */}
        {showCreateIngredientModal && (
          <Modal
            isOpen={showCreateIngredientModal}
            onClose={() => {
              setShowCreateIngredientModal(false);
              setNewIngredientData({ nom: '', description: '', allergenes: [] });
              setNewIngredientAllergenes('');
              setIngredientNameError('');
            }}
            title="Créer un nouvel ingrédient"
            maxWidth="max-w-md"
          >
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'ingrédient *
                </label>
                <input
                  type="text"
                  value={newIngredientData.nom}
                  onChange={(e) => {
                    setNewIngredientData({ ...newIngredientData, nom: e.target.value });
                    if (ingredientNameError) setIngredientNameError('');
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${ingredientNameError ? 'border-red-400' : 'border-gray-300'}`}
                  placeholder="Ex: Saumon, Riz, Avocat..."
                  autoFocus
                />
                {ingredientNameError && (
                  <p className="text-xs text-red-600 mt-1">{ingredientNameError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optionnel)
                </label>
                <textarea
                  value={newIngredientData.description}
                  onChange={(e) => setNewIngredientData({ ...newIngredientData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Description de l'ingrédient..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allergènes (optionnel)
                </label>
                <input
                  type="text"
                  value={newIngredientAllergenes}
                  onChange={(e) => setNewIngredientAllergenes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Séparez par des virgules (ex: Gluten, Lait, Œufs)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Liste les allergènes présents dans cet ingrédient
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCreateIngredientModal(false);
                    setNewIngredientData({ nom: '', description: '', allergenes: [] });
                    setNewIngredientAllergenes('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreateIngredient}
                  disabled={creatingIngredient || !newIngredientData.nom.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creatingIngredient ? 'Création...' : 'Créer l\'ingrédient'}
                </button>
              </div>
            </div>
          </Modal>
        )}

        <ConfirmDialog
          isOpen={changeArchetypeConfirmOpen}
          onClose={() => setChangeArchetypeConfirmOpen(false)}
          onConfirm={confirmChangeArchetype}
          title="Changer de modèle produit ?"
          message="Les informations déjà saisies dans cette fiche seront perdues. Vous retournerez à l'assistant pour choisir un autre modèle."
          confirmLabel="Changer de modèle"
          cancelLabel="Continuer la saisie"
          variant="danger"
        />

        {/* Dialog d'erreur détaillée avec bouton de copie */}
        {errorDialog.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {errorDialog.title || 'Erreur'}
                </h3>
                <button
                  className="p-2 rounded-full hover:bg-gray-100"
                  onClick={() => setErrorDialog({ open: false, title: '', message: '', raw: null })}
                >
                  <X size={18} className="text-gray-500" />
                </button>
              </div>

              <p className="text-sm text-gray-700 mb-3">
                Vous pouvez copier ci-dessous tous les détails de l'erreur pour les envoyer ou les garder en note.
              </p>

              <textarea
                readOnly
                className="w-full h-48 text-xs font-mono border border-gray-300 rounded-lg p-3 bg-gray-50 text-gray-800 resize-none"
                value={errorDialog.message}
              />

              <div className="mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-100"
                  onClick={() => setErrorDialog({ open: false, title: '', message: '', raw: null })}
                >
                  Fermer
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(errorDialog.message || '');
                      alert('Erreur copiée dans le presse-papiers.');
                    } catch (e) {
                      console.error('Impossible de copier dans le presse-papiers:', e);
                      alert('Impossible de copier automatiquement. Vous pouvez copier le texte manuellement.');
                    }
                  }}
                >
                  Copier l'erreur
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProduitModal;
